import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type {
  EvaluationQuestion,
  EvaluationTemplate,
  IndexItem,
  IntegrationApiKeyDto,
  Moderator,
  OfferTemplate,
  Presenter,
  PublicSurveySubmission,
  PublicWebinarMe,
  PublicWebinarSubscriptionForm,
  QuitPage,
  QuizAnswerOption,
  QuizQuestion,
  QuizSubmission,
  QuizTemplate,
  EvergreenChatConfiguration,
  EvergreenConfiguration,
  EvergreenTimelineEvent,
  ReplayConfiguration,
  SessionInvite,
  SlideUploadInit,
  Speaker,
  SurveyAnswerOption,
  SurveyQuestion,
  SurveyTemplate,
  TechnicalAuthUser,
  ThankYouPage,
  WaitingRoomPage,
  SharedResource,
  SharedResourcePage,
  SharedResourcePublic,
  WebhookDto,
  Webinar,
  WebinarIndex,
  WebinarParticipationStatusConfigDto,
  WebinarResource,
  WebinarResourceUploadInit,
  WebinarSession,
  WebinarSessionAttendee,
  WebinarSessionDetail,
  WebinarSlide,
  WebinarSlideNote,
  WebinarState,
  WebinarSubscriptionPage,
  WebinarForEvergreen,
  WebinarIntegrationTrigger,
} from '@/features/webinar/types';
import type { WorkspaceTeamMember } from '@/features/workspace/types';
import {
  bulkUpsertWebinarSlideNotes,
  createAutomationTrigger,
  createEvaluationQuestion,
  createIndexItem,
  createIntegrationApiKey,
  createModerator,
  createOfferTemplate,
  createPresenter,
  createQuitPageReason,
  createQuizAnswerOption,
  createQuizQuestion,
  createQuizTemplate,
  createSpeaker,
  createSurveyAnswerOption,
  createSurveyQuestion,
  createSurveyTemplate,
  createWebhook,
  addEvergreenChatMessage,
  updateEvergreenChatMessage,
  deleteEvergreenChatMessages,
  saveEvergreenSources,
  importEvergreenChat,
  createWebinar,
  createWorkspaceInvitation,
  deleteAutomationTrigger,
  deleteEvaluationQuestion,
  deleteIndexItem,
  deleteModerator,
  deleteOfferTemplate,
  deleteOfferTemplateContent,
  deleteOfferTemplateContentImage,
  deletePresenter,
  deleteQuitPageReason,
  deleteQuitPageReasonImage,
  deleteQuizAnswerOption,
  deleteQuizQuestion,
  deleteQuizTemplate,
  deleteSessionAttendee,
  deleteSpeaker,
  deleteSurveyAnswerOption,
  deleteSurveyQuestion,
  deleteSurveyTemplate,
  deleteThankYouPageImage,
  deleteWebhook,
  deleteWebinar,
  deleteWebinarResource,
  deleteWebinarSlide,
  cloneWebinar,
  duplicateWebinar,
  exportSessionAttendees,
  importSessionAttendees,
  fetchAvailableWebinars,
  fetchEvaluationQuestions,
  fetchEvaluationTemplate,
  fetchIntegrationApiKeys,
  fetchIntegrationsMe,
  fetchModeratorById,
  fetchMyPublicQuizSubmission,
  fetchMyPublicSurveySubmission,
  fetchOfferTemplate,
  fetchParticipationStatuses,
  fetchPresenterById,
  fetchPublicWaitingRoomPage,
  fetchPublicWaitingRoomQuiz,
  fetchPublicWaitingRoomSurvey,
  fetchPublicWebinarIndex,
  fetchPublicWebinarMe,
  fetchPublicWebinarSession,
  fetchPublicWebinarSubscriptionForm,
  fetchQuitPage,
  fetchQuizTemplate,
  fetchEvergreenChatConfiguration,
  fetchEvergreenConfiguration,
  fetchEvergreenTimeline,
  fetchReplayConfiguration,
  replaceEvergreenTimeline,
  deleteEvergreenTimeline,
  fetchSessionInvites,
  fetchSpeakerById,
  fetchSurveyTemplate,
  fetchThankYouPage,
  fetchWaitingRoomPage,
  fetchWebinarById,
  fetchWebinarIndex,
  fetchWebinars,
  fetchWebinarSessionConfiguration,
  fetchWebinarSessionDetail,
  fetchSessionChatExport,
  fetchWebinarSlide,
  fetchWebinarSubscriptionPage,
  addBannedWords,
  finalizeWebinarResourceUpload,
  finalizeWebinarSlideUpload,
  importBannedWordsFromCsv,
  removeBannedWords,
  generateWebinarSlideUploadUrl,
  getWebinarSession,
  initWebinarResourceUpload,
  listAutomationTriggers,
  listIndexItems,
  listModerators,
  listOfferTemplates,
  listPresenters,
  listQuitPageReasons,
  listQuizAnswerOptions,
  listQuizQuestions,
  listQuizTemplates,
  listSessionAttendees,
  listSpeakers,
  listSharedResources,
  createSharedResourceLink,
  finalizeSharedResourceFileUpload,
  deleteSharedResource,
  fetchCurrentSharedResources,
  adminShowSharedResource,
  adminHideSharedResource,
  listSurveyAnswerOptions,
  listSurveyQuestions,
  listSurveyTemplates,
  listWebhooks,
  listWebinarResources,
  listWebinarSessions,
  listWebinarSlideNotes,
  listWebinarSlides,
  listWorkspaceAccesses,
  publishWebinar,
  registerPublicWebinar,
  registerPublicWebinarReplay,
  replaceIndexItems,
  replaceParticipationStatuses,
  revokeIntegrationApiKey,
  rotateIntegrationApiKey,
  submitPublicQuiz,
  submitPublicQuizPut,
  submitPublicSurvey,
  submitPublicSurveyPut,
  submitPublicWaitingRoomSurvey,
  unpublishWebinar,
  updateAutomationTrigger,
  updateEvaluationQuestion,
  updateEvaluationTemplate,
  updateIndexItem,
  updateModerator,
  updateOfferTemplate,
  updatePresenter,
  updateQuitPageReason,
  updateQuizAnswerOption,
  updateQuizQuestion,
  updateQuizTemplate,
  updateEvergreenChatConfiguration,
  updateEvergreenConfiguration,
  updateReplayConfiguration,
  updateSpeaker,
  updateSurveyAnswerOption,
  updateSurveyQuestion,
  updateSurveyTemplate,
  updateThankYouPage,
  updateWaitingRoomPage,
  updateWebhook,
  updateWebinar,
  updateWebinarSessionConfiguration,
  updateWebinarSlideNote,
  updateWebinarSubscriptionPage,
  uploadOfferTemplateContentImage,
  uploadQuitPageReasonImage,
  uploadThankYouPageImage,
  uploadWaitingRoomPageBackgroundImage,
  uploadWaitingRoomPageImage,
  uploadWaitingRoomPageVideo,
  deleteWaitingRoomPageMainAsset,
  uploadWebinarSubscriptionPageBackgroundImage,
  upsertOfferTemplateContent,
  upsertQuitPage,
} from './webinarThunks';
import { logout } from '@/features/auth/authThunks';
import { switchWorkspace } from '@/features/workspace/workspaceSlice';
import { fetchPublicSession } from '@/features/live/liveThunks';
import { isPublicSessionApplicable } from '@/lib/publicSessionFreshness';
import {
  isForActivePublicSession,
  isTransientPublicReadFailure,
  publicSessionReadKey,
} from '@/lib/publicSessionRead';

const initialState: WebinarState = {
  webinars: [],
  webinarsTotalElements: 0,
  webinarsTotalPages: 0,
  loading: false,
  error: null,
  createWebinarInitialLoading: false,
  currentWebinar: null,
  currentWebinarIndex: null,
  indexLoading: false,
  indexError: null,
  currentWebinarSubscriptionPage: null,
  subscriptionPageLoading: false,
  subscriptionPageError: null,
  currentReplayConfiguration: null,
  replayConfigurationLoading: false,
  replayConfigurationError: null,
  currentEvergreenConfiguration: null,
  evergreenConfigurationLoading: false,
  evergreenConfigurationError: null,
  evergreenTimeline: [],
  evergreenTimelineLoading: false,
  evergreenTimelineError: null,
  availableWebinars: [],
  availableWebinarsTotalElements: 0,
  availableWebinarsTotalPages: 0,
  availableWebinarsLoading: false,
  availableWebinarsError: null,
  evergreenConvertLoading: false,
  evergreenConvertError: null,
  evergreenChatImportLoading: false,
  evergreenChatImportError: null,
  currentEvergreenChatConfiguration: null,
  evergreenChatConfigurationLoading: false,
  evergreenChatConfigurationError: null,
  currentSessionConfiguration: null,
  sessionConfigurationWebinarId: null,
  sessionConfigurationLoading: false,
  sessionConfigurationError: null,
  webinarSessions: [],
  webinarSessionsLoading: false,
  webinarSessionsError: null,
  currentWebinarSession: null,
  currentWebinarSessionLoading: false,
  currentWebinarSessionError: null,
  sessionDetailsBySessionId: {},
  sessionDetailsLoading: false,
  sessionDetailsError: null,
  sessionChatExportLoading: false,
  sessionChatExportError: null,
  currentWaitingRoomPage: null,
  waitingRoomPageLoading: false,
  waitingRoomPageError: null,
  currentThankYouPage: null,
  thankYouPageLoading: false,
  thankYouPageError: null,
  offerTemplates: [],
  offerTemplatesLoading: false,
  offerTemplatesError: null,
  currentOfferTemplate: null,
  currentOfferTemplateLoading: false,
  currentOfferTemplateError: null,
  surveyTemplates: [],
  surveyTemplatesLoading: false,
  surveyTemplatesError: null,
  currentSurveyTemplate: null,
  currentSurveyTemplateLoading: false,
  currentSurveyTemplateError: null,
  surveyQuestions: [],
  surveyQuestionsLoading: false,
  surveyQuestionsError: null,
  surveyAnswerOptions: [],
  surveyAnswerOptionsLoading: false,
  surveyAnswerOptionsError: null,
  currentQuitPage: null,
  quitPageLoading: false,
  quitPageError: null,
  webinarResources: [],
  webinarResourcesTotalElements: 0,
  webinarResourcesTotalPages: 0,
  webinarResourcesLoading: false,
  webinarResourcesError: null,
  webinarResourceUploadInit: null,
  webinarResourceUploadInitLoading: false,
  webinarResourceUploadInitError: null,
  webinarResourceFinalizeLoading: false,
  webinarResourceFinalizeError: null,
  sharedResources: [],
  sharedResourcesLoading: false,
  sharedResourcesError: null,
  sharedResourcesPage: 0,
  sharedResourcesTotalPages: 0,
  sharedResourcesTotalElements: 0,
  currentSharedResources: [],
  webinarSlides: [],
  webinarSlidesLoading: false,
  webinarSlidesError: null,
  currentWebinarSlide: null,
  currentWebinarSlideLoading: false,
  currentWebinarSlideError: null,
  webinarSlideUploadInit: null,
  webinarSlideUploadInitLoading: false,
  webinarSlideUploadInitError: null,
  webinarSlideFinalizeLoading: false,
  webinarSlideFinalizeError: null,
  webinarSlideNotes: [],
  webinarSlideNotesLoading: false,
  webinarSlideNotesError: null,
  webinarSlideNotesSaving: false,
  webinarSlideNotesSavingError: null,
  webinarSlideNoteUpdating: false,
  webinarSlideNoteUpdatingError: null,
  quizTemplates: [],
  quizTemplatesLoading: false,
  quizTemplatesError: null,
  currentQuizTemplate: null,
  currentQuizTemplateLoading: false,
  currentQuizTemplateError: null,
  quizQuestions: [],
  quizQuestionsLoading: false,
  quizQuestionsError: null,
  quizAnswerOptions: [],
  quizAnswerOptionsLoading: false,
  quizAnswerOptionsError: null,
  speakers: [],
  speakersLoading: false,
  speakersError: null,
  currentSpeaker: null,
  currentSpeakerLoading: false,
  currentSpeakerError: null,
  moderators: [],
  moderatorsLoading: false,
  moderatorsError: null,
  currentModerator: null,
  currentModeratorLoading: false,
  currentModeratorError: null,
  presenters: [],
  presentersLoading: false,
  presentersError: null,
  currentPresenter: null,
  currentPresenterLoading: false,
  currentPresenterError: null,
  sessionInvites: [],
  sessionInvitesLoading: false,
  sessionInvitesError: null,
  participationStatuses: [],
  showParticipationStatus: false,
  participationStatusesLoading: false,
  participationStatusesError: null,
  currentPublicSession: null,
  publicSessionLoading: false,
  publicSessionError: null,
  publicSessionErrorTransient: false,
  publicSessionRequestId: null,
  publicSessionKey: null,
  currentPublicSubscriptionForm: null,
  publicSubscriptionFormLoading: false,
  publicSubscriptionFormError: null,
  currentPublicWebinarIndex: null,
  publicWebinarIndexLoading: false,
  publicWebinarIndexError: null,
  currentPublicWaitingRoomSurvey: null,
  publicWaitingRoomSurveyLoading: false,
  publicWaitingRoomSurveyError: null,
  currentPublicWaitingRoomQuiz: null,
  publicWaitingRoomQuizLoading: false,
  publicWaitingRoomQuizError: null,
  currentPublicWaitingRoomPage: null,
  publicWaitingRoomPageLoading: false,
  publicWaitingRoomPageError: null,
  publicWebinarRegisterLoading: false,
  publicWebinarRegisterError: null,
  publicSurveySubmitLoading: false,
  publicSurveySubmitError: null,
  publicQuizSubmitLoading: false,
  publicQuizSubmitError: null,
  currentPublicSurveySubmission: null,
  publicSurveySubmissionLoading: false,
  publicSurveySubmissionError: null,
  currentPublicQuizSubmission: null,
  publicQuizSubmissionLoading: false,
  publicQuizSubmissionError: null,
  currentPublicWebinarMe: null,
  publicWebinarMeLoading: false,
  publicWebinarMeError: null,
  publicWaitingRoomSurveySubmitLoading: false,
  publicWaitingRoomSurveySubmitError: null,
  sessionAttendees: [],
  sessionAttendeesTotalElements: 0,
  sessionAttendeesLoading: false,
  sessionAttendeesError: null,
  sessionAttendeesExport: {
    loading: false,
    error: null,
  },
  sessionAttendeesImport: {
    loading: false,
    error: null,
    result: null,
  },
  currentIntegrationsMe: null,
  integrationsMeLoading: false,
  integrationsMeError: null,
  workspaceAccesses: [],
  workspaceAccessesLoading: false,
  workspaceAccessesError: null,
  evaluationTemplate: null,
  evaluationTemplateLoading: false,
  evaluationTemplateError: null,
  evaluationQuestions: [],
  evaluationQuestionsLoading: false,
  evaluationQuestionsError: null,
  createEvaluationQuestionLoading: false,
  createEvaluationQuestionError: null,
  updateEvaluationTemplateLoading: false,
  updateEvaluationTemplateError: null,
  updateEvaluationQuestionLoading: false,
  updateEvaluationQuestionError: null,
  deleteEvaluationQuestionLoading: false,
  deleteEvaluationQuestionError: null,
  webhooks: [],
  webhooksLoading: false,
  webhooksError: null,
  updateWebhookLoading: false,
  updateWebhookError: null,
  deleteWebhookLoading: false,
  deleteWebhookError: null,
  integrationApiKeys: [],
  integrationApiKeysLoading: false,
  integrationApiKeysError: null,
  automationTriggers: [],
  automationTriggersLoading: false,
  automationTriggersError: null,
  updateAutomationTriggerLoading: false,
  updateAutomationTriggerError: null,
  deleteAutomationTriggerLoading: false,
  deleteAutomationTriggerError: null,
};

const slice = createSlice({
  name: 'webinar',
  initialState,
  reducers: {
    setCurrentWebinar: (state, action: PayloadAction<Webinar | null>) => {
      state.currentWebinar = action.payload;
    },
    setCreateWebinarInitialLoading: (state, action: PayloadAction<boolean>) => {
      state.createWebinarInitialLoading = action.payload;
    },
    // Applique un événement live Ably de diffusion/masquage d'une ressource (Fichiers & Liens).
    applySharedResourceLiveEvent: (
      state,
      action: PayloadAction<
        | { type: 'SHARED_RESOURCE_SHOW'; resource: SharedResourcePublic }
        | { type: 'SHARED_RESOURCE_HIDE'; resource: { id: number } }
      >,
    ) => {
      const { type, resource } = action.payload;
      if (type === 'SHARED_RESOURCE_SHOW') {
        const index = state.currentSharedResources.findIndex((r) => r.id === resource.id);
        if (index >= 0) {
          state.currentSharedResources[index] = resource;
        } else {
          state.currentSharedResources.push(resource);
        }
      } else {
        state.currentSharedResources = state.currentSharedResources.filter(
          (r) => r.id !== resource.id,
        );
      }
    },
    clearCurrentSharedResources: (state) => {
      state.currentSharedResources = [];
    },
    clearCurrentWebinar: (state) => {
      const currentWebinarId = state.currentWebinar?.id;
      state.currentWebinar = null;
      state.currentWebinarSession = null;
      state.currentWebinarSessionError = null;
      state.currentSessionConfiguration = null;
      state.sessionConfigurationWebinarId = null;
      state.sessionConfigurationLoading = false;
      state.sessionConfigurationError = null;

      if (currentWebinarId) {
        // Clear speakers, moderators and presenters
        state.speakers = state.speakers.filter((s) => s.webinarId !== currentWebinarId);
        state.moderators = state.moderators.filter((m) => m.webinarId !== currentWebinarId);
        state.presenters = state.presenters.filter((p) => p.webinarId !== currentWebinarId);

        if (state.currentPresenter?.webinarId === currentWebinarId) {
          state.currentPresenter = null;
          state.currentPresenterLoading = false;
          state.currentPresenterError = null;
        }

        // Clear waiting room page
        if (state.currentWaitingRoomPage?.webinarId === currentWebinarId) {
          state.currentWaitingRoomPage = null;
          state.waitingRoomPageLoading = false;
          state.waitingRoomPageError = null;
        }

        // Clear subscription page (signin page)
        if (state.currentWebinarSubscriptionPage?.webinarId === currentWebinarId) {
          state.currentWebinarSubscriptionPage = null;
          state.subscriptionPageLoading = false;
          state.subscriptionPageError = null;
        }

        // Clear thank you page
        if (state.currentThankYouPage?.webinarId === currentWebinarId) {
          state.currentThankYouPage = null;
          state.thankYouPageLoading = false;
          state.thankYouPageError = null;
        }

        // Clear replay configuration
        if (state.currentReplayConfiguration?.webinarId === currentWebinarId) {
          state.currentReplayConfiguration = null;
          state.replayConfigurationLoading = false;
          state.replayConfigurationError = null;
        }

        // Clear evergreen configuration
        if (state.currentEvergreenConfiguration?.webinarId === currentWebinarId) {
          state.currentEvergreenConfiguration = null;
          state.evergreenConfigurationLoading = false;
          state.evergreenConfigurationError = null;
        }
        state.evergreenTimeline = [];
        state.evergreenTimelineLoading = false;
        state.evergreenTimelineError = null;

        state.evergreenChatImportLoading = false;
        state.evergreenChatImportError = null;

        // Clear PDFs (webinar resources)
        state.webinarResources = state.webinarResources.filter(
          (r) => r.webinarId !== currentWebinarId,
        );
        state.webinarResourcesTotalElements = 0;
        state.webinarResourcesTotalPages = 0;
        state.webinarResourcesLoading = false;
        state.webinarResourcesError = null;
        state.webinarResourceUploadInit = null;
        state.webinarResourceUploadInitLoading = false;
        state.webinarResourceUploadInitError = null;
        state.webinarResourceFinalizeLoading = false;
        state.webinarResourceFinalizeError = null;

        // Clear Videos (webinar slides)
        state.webinarSlides = state.webinarSlides.filter((s) => s.webinarId !== currentWebinarId);
        state.webinarSlidesLoading = false;
        state.webinarSlidesError = null;
        state.currentWebinarSlide = null;
        state.currentWebinarSlideLoading = false;
        state.currentWebinarSlideError = null;
        state.webinarSlideUploadInit = null;
        state.webinarSlideUploadInitLoading = false;
        state.webinarSlideUploadInitError = null;
        state.webinarSlideFinalizeLoading = false;
        state.webinarSlideFinalizeError = null;
        state.webinarSlideNotes = [];
        state.webinarSlideNotesLoading = false;
        state.webinarSlideNotesError = null;
        state.webinarSlideNotesSaving = false;
        state.webinarSlideNotesSavingError = null;
        state.webinarSlideNoteUpdating = false;
        state.webinarSlideNoteUpdatingError = null;

        // Clear offers
        state.offerTemplates = state.offerTemplates.filter((o) => o.webinarId !== currentWebinarId);
        state.offerTemplatesLoading = false;
        state.offerTemplatesError = null;
        if (state.currentOfferTemplate?.webinarId === currentWebinarId) {
          state.currentOfferTemplate = null;
          state.currentOfferTemplateLoading = false;
          state.currentOfferTemplateError = null;
        }

        // Clear surveys
        state.surveyTemplates = state.surveyTemplates.filter(
          (s) => s.webinarId !== currentWebinarId,
        );
        state.surveyTemplatesLoading = false;
        state.surveyTemplatesError = null;
        if (state.currentSurveyTemplate?.webinarId === currentWebinarId) {
          state.currentSurveyTemplate = null;
          state.currentSurveyTemplateLoading = false;
          state.currentSurveyTemplateError = null;
        }
        state.surveyQuestions = [];
        state.surveyQuestionsLoading = false;
        state.surveyQuestionsError = null;
        state.surveyAnswerOptions = [];
        state.surveyAnswerOptionsLoading = false;
        state.surveyAnswerOptionsError = null;

        // Clear quizs
        state.quizTemplates = state.quizTemplates.filter((q) => q.webinarId !== currentWebinarId);
        state.quizTemplatesLoading = false;
        state.quizTemplatesError = null;
        if (state.currentQuizTemplate?.webinarId === currentWebinarId) {
          state.currentQuizTemplate = null;
          state.currentQuizTemplateLoading = false;
          state.currentQuizTemplateError = null;
        }
        state.quizQuestions = [];
        state.quizQuestionsLoading = false;
        state.quizQuestionsError = null;
        state.quizAnswerOptions = [];
        state.quizAnswerOptionsLoading = false;
        state.quizAnswerOptionsError = null;

        // Clear quit form
        if (state.currentQuitPage?.webinarId === currentWebinarId) {
          state.currentQuitPage = null;
          state.quitPageLoading = false;
          state.quitPageError = null;
        }
        if (state.currentWebinarIndex?.webinarId === currentWebinarId) {
          state.currentWebinarIndex = null;
          state.indexLoading = false;
          state.indexError = null;
        }
      }
    },
    reset: () => initialState,
  },
  extraReducers: (builder) => {
    builder.addCase(createWebinar.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(createWebinar.fulfilled, (state) => {
      state.loading = false;
      state.error = null;
    });
    builder.addCase(createWebinar.rejected, (state) => {
      state.loading = false;
    });

    builder.addCase(duplicateWebinar.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(duplicateWebinar.fulfilled, (state) => {
      state.loading = false;
      state.error = null;
    });
    builder.addCase(duplicateWebinar.rejected, (state, action) => {
      state.loading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.error = payload?.message ?? action.error.message ?? 'Failed to duplicate webinar';
    });

    builder.addCase(cloneWebinar.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(cloneWebinar.fulfilled, (state) => {
      state.loading = false;
      state.error = null;
    });
    builder.addCase(cloneWebinar.rejected, (state) => {
      state.loading = false;
      // L'échec de duplication est notifié à l'utilisateur via un toast dédié dans la modale ;
      // on n'alimente pas l'erreur globale pour ne pas remplacer toute la page par un écran d'erreur.
      state.error = null;
    });

    builder.addCase(fetchWebinars.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(
      fetchWebinars.fulfilled,
      (
        state,
        action: PayloadAction<{
          content: Webinar[];
          totalElements: number;
          totalPages: number;
          size: number;
          number: number;
        }>,
      ) => {
        state.loading = false;
        state.webinars = action.payload.content;
        state.webinarsTotalElements = action.payload.totalElements;
        state.webinarsTotalPages = action.payload.totalPages;
        state.error = null;
      },
    );
    builder.addCase(fetchWebinars.rejected, (state, action) => {
      state.loading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.error = payload?.message ?? action.error.message ?? 'Failed to fetch webinars';
    });

    builder.addCase(fetchAvailableWebinars.pending, (state) => {
      state.availableWebinarsLoading = true;
      state.availableWebinarsError = null;
    });
    builder.addCase(
      fetchAvailableWebinars.fulfilled,
      (
        state,
        action: PayloadAction<{
          content: WebinarForEvergreen[];
          totalElements: number;
          totalPages: number;
        }>,
      ) => {
        state.availableWebinarsLoading = false;
        state.availableWebinars = action.payload.content;
        state.availableWebinarsTotalElements = action.payload.totalElements;
        state.availableWebinarsTotalPages = action.payload.totalPages;
        state.availableWebinarsError = null;
      },
    );
    builder.addCase(fetchAvailableWebinars.rejected, (state, action) => {
      state.availableWebinarsLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.availableWebinarsError =
        payload?.message ?? action.error.message ?? 'Failed to fetch available webinars';
    });

    builder.addCase(fetchWebinarById.pending, (state, action) => {
      state.loading = true;
      state.error = null;
      // Réinitialiser la session quand on charge un autre webinaire pour éviter d'afficher des erreurs obsolètes.
      // Un store encore vide (premier chargement, fetch en double) n'est pas un changement de webinaire :
      // le reset jetterait la configuration de session en vol via la garde de son fulfilled.
      const newWebinarId = (action.meta.arg as { id?: number })?.id;
      const previousWebinarId = state.currentWebinar?.id;
      if (newWebinarId != null && previousWebinarId != null && previousWebinarId !== newWebinarId) {
        state.currentWebinarSession = null;
        state.currentWebinarSessionError = null;
      }
      if (
        newWebinarId != null &&
        state.sessionConfigurationWebinarId != null &&
        state.sessionConfigurationWebinarId !== newWebinarId
      ) {
        state.currentSessionConfiguration = null;
        state.sessionConfigurationWebinarId = null;
      }
    });
    builder.addCase(fetchWebinarById.fulfilled, (state, action: PayloadAction<Webinar>) => {
      state.loading = false;
      state.currentWebinar = action.payload;
      state.error = null;

      const index = state.webinars.findIndex((w) => w.id === action.payload.id);
      if (index >= 0) {
        state.webinars[index] = action.payload;
      } else {
        state.webinars.push(action.payload);
      }
    });
    builder.addCase(fetchWebinarById.rejected, (state, action) => {
      state.loading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.error = payload?.message ?? action.error.message ?? 'Failed to fetch webinar';
    });

    builder.addCase(updateWebinar.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(updateWebinar.fulfilled, (state, action: PayloadAction<Webinar>) => {
      state.loading = false;
      state.error = null;

      const index = state.webinars.findIndex((w) => w.id === action.payload.id);
      if (index >= 0) {
        state.webinars[index] = action.payload;
      }

      if (state.currentWebinar?.id === action.payload.id) {
        state.currentWebinar = action.payload;
      }
    });
    builder.addCase(updateWebinar.rejected, (state) => {
      state.loading = false;
    });

    builder.addCase(importBannedWordsFromCsv.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(importBannedWordsFromCsv.fulfilled, (state, action: PayloadAction<Webinar>) => {
      state.loading = false;
      state.error = null;

      const index = state.webinars.findIndex((w) => w.id === action.payload.id);
      if (index >= 0) {
        state.webinars[index] = action.payload;
      }

      if (state.currentWebinar?.id === action.payload.id) {
        state.currentWebinar = action.payload;
      }
    });
    builder.addCase(importBannedWordsFromCsv.rejected, (state, action) => {
      state.loading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.error =
        payload?.message ?? action.error.message ?? 'Failed to import banned words from CSV';
    });

    builder.addCase(addBannedWords.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(addBannedWords.fulfilled, (state, action: PayloadAction<Webinar>) => {
      state.loading = false;
      state.error = null;

      const index = state.webinars.findIndex((w) => w.id === action.payload.id);
      if (index >= 0) {
        state.webinars[index] = action.payload;
      }

      if (state.currentWebinar?.id === action.payload.id) {
        state.currentWebinar = action.payload;
      }
    });
    builder.addCase(addBannedWords.rejected, (state, action) => {
      state.loading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.error = payload?.message ?? action.error.message ?? 'Failed to add banned words';
    });

    builder.addCase(removeBannedWords.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(removeBannedWords.fulfilled, (state, action: PayloadAction<Webinar>) => {
      state.loading = false;
      state.error = null;

      const index = state.webinars.findIndex((w) => w.id === action.payload.id);
      if (index >= 0) {
        state.webinars[index] = action.payload;
      }

      if (state.currentWebinar?.id === action.payload.id) {
        state.currentWebinar = action.payload;
      }
    });
    builder.addCase(removeBannedWords.rejected, (state, action) => {
      state.loading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.error = payload?.message ?? action.error.message ?? 'Failed to remove banned words';
    });

    builder.addCase(deleteWebinar.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(deleteWebinar.fulfilled, (state, action: PayloadAction<number>) => {
      state.loading = false;
      state.error = null;
      state.webinars = state.webinars.filter((w) => w.id !== action.payload);
      if (state.currentWebinar?.id === action.payload) {
        state.currentWebinar = null;
      }
      if (state.currentWebinarIndex?.webinarId === action.payload) {
        state.currentWebinarIndex = null;
      }
      if (state.currentWebinarSubscriptionPage?.webinarId === action.payload) {
        state.currentWebinarSubscriptionPage = null;
      }
      if (state.currentReplayConfiguration?.webinarId === action.payload) {
        state.currentReplayConfiguration = null;
      }
      if (state.currentEvergreenConfiguration?.webinarId === action.payload) {
        state.currentEvergreenConfiguration = null;
      }
      state.evergreenTimeline = [];
      state.evergreenTimelineLoading = false;
      state.evergreenTimelineError = null;
      state.evergreenChatImportLoading = false;
      state.evergreenChatImportError = null;
      if (state.currentWaitingRoomPage?.webinarId === action.payload) {
        state.currentWaitingRoomPage = null;
      }
      if (state.currentThankYouPage?.webinarId === action.payload) {
        state.currentThankYouPage = null;
      }
      // Supprimer les offres associées au webinar
      state.offerTemplates = state.offerTemplates.filter((o) => o.webinarId !== action.payload);
      if (state.currentOfferTemplate?.webinarId === action.payload) {
        state.currentOfferTemplate = null;
      }
      // Supprimer les surveys associés au webinar
      state.surveyTemplates = state.surveyTemplates.filter((s) => s.webinarId !== action.payload);
      if (state.currentSurveyTemplate?.webinarId === action.payload) {
        state.currentSurveyTemplate = null;
      }
      state.surveyQuestions = [];
      state.surveyAnswerOptions = [];
      // Supprimer les quizzes associés au webinar
      state.quizTemplates = state.quizTemplates.filter((q) => q.webinarId !== action.payload);
      if (state.currentQuizTemplate?.webinarId === action.payload) {
        state.currentQuizTemplate = null;
      }
      state.quizQuestions = [];
      state.quizAnswerOptions = [];
      state.speakers = [];
      state.speakersLoading = false;
      state.speakersError = null;
      state.currentSpeaker = null;
      state.currentSpeakerLoading = false;
      state.currentSpeakerError = null;
      state.moderators = [];
      state.moderatorsLoading = false;
      state.moderatorsError = null;
      state.currentModerator = null;
      state.currentModeratorLoading = false;
      state.currentModeratorError = null;
      state.presenters = [];
      state.presentersLoading = false;
      state.presentersError = null;
      state.currentPresenter = null;
      state.currentPresenterLoading = false;
      state.currentPresenterError = null;
      state.webinarResources = [];
      state.webinarResourcesTotalElements = 0;
      state.webinarResourcesTotalPages = 0;
      state.webinarResourcesLoading = false;
      state.webinarResourcesError = null;
      state.webinarResourceUploadInit = null;
      state.webinarResourceUploadInitLoading = false;
      state.webinarResourceUploadInitError = null;
      state.webinarResourceFinalizeLoading = false;
      state.webinarResourceFinalizeError = null;
      state.webinarSlides = [];
      state.webinarSlidesLoading = false;
      state.webinarSlidesError = null;
      state.currentWebinarSlide = null;
      state.currentWebinarSlideLoading = false;
      state.currentWebinarSlideError = null;
      state.webinarSlideUploadInit = null;
      state.webinarSlideUploadInitLoading = false;
      state.webinarSlideUploadInitError = null;
      state.webinarSlideFinalizeLoading = false;
      state.webinarSlideFinalizeError = null;
      state.webinarSlideNotes = [];
      state.webinarSlideNotesLoading = false;
      state.webinarSlideNotesError = null;
      state.webinarSlideNotesSaving = false;
      state.webinarSlideNotesSavingError = null;
      state.webinarSlideNoteUpdating = false;
      state.webinarSlideNoteUpdatingError = null;
      state.webinarSlideNotes = [];
      state.webinarSlideNotesLoading = false;
      state.webinarSlideNotesError = null;
      state.webinarSlideNotesSaving = false;
      state.webinarSlideNotesSavingError = null;
      state.webinarSlideNoteUpdating = false;
      state.webinarSlideNoteUpdatingError = null;
      state.webinarSlides = [];
      state.webinarSlidesLoading = false;
      state.webinarSlidesError = null;
      state.currentWebinarSlide = null;
      state.currentWebinarSlideLoading = false;
      state.currentWebinarSlideError = null;
      state.webinarSlideUploadInit = null;
      state.webinarSlideUploadInitLoading = false;
      state.webinarSlideUploadInitError = null;
      state.webinarSlideFinalizeLoading = false;
      state.webinarSlideFinalizeError = null;
    });
    // Échec d'action, pas de chargement : les pages traitent `error` comme une erreur de chargement
    // et afficheraient un écran d'erreur. Le refus (webinaire source d'evergreen) est notifié par
    // l'appelant depuis le payload rejeté.
    builder.addCase(deleteWebinar.rejected, (state) => {
      state.loading = false;
    });

    builder.addCase(unpublishWebinar.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(unpublishWebinar.fulfilled, (state, action: PayloadAction<Webinar>) => {
      state.loading = false;
      state.error = null;

      const index = state.webinars.findIndex((w) => w.id === action.payload.id);
      if (index >= 0) {
        state.webinars[index] = action.payload;
      }

      if (state.currentWebinar?.id === action.payload.id) {
        state.currentWebinar = action.payload;
      }
    });
    // Un refus de (dé)publication est signalé par l'appelant : `error` remplacerait la page.
    builder.addCase(unpublishWebinar.rejected, (state) => {
      state.loading = false;
    });

    builder.addCase(publishWebinar.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(publishWebinar.fulfilled, (state, action: PayloadAction<Webinar>) => {
      state.loading = false;
      state.error = null;

      const index = state.webinars.findIndex((w) => w.id === action.payload.id);
      if (index >= 0) {
        state.webinars[index] = action.payload;
      }

      if (state.currentWebinar?.id === action.payload.id) {
        state.currentWebinar = action.payload;
      }
    });
    builder.addCase(publishWebinar.rejected, (state) => {
      state.loading = false;
    });

    builder.addCase(logout.fulfilled, (state) => {
      state.webinars = [];
      state.webinarsTotalElements = 0;
      state.webinarsTotalPages = 0;
      state.currentWebinar = null;
      state.error = null;
      state.currentWebinarIndex = null;
      state.indexError = null;
      state.currentWebinarSubscriptionPage = null;
      state.subscriptionPageError = null;
      state.currentReplayConfiguration = null;
      state.replayConfigurationError = null;
      state.currentEvergreenConfiguration = null;
      state.evergreenConfigurationError = null;
      state.evergreenTimeline = [];
      state.evergreenTimelineLoading = false;
      state.evergreenTimelineError = null;
      state.evergreenChatImportLoading = false;
      state.evergreenChatImportError = null;
      state.currentSessionConfiguration = null;
      state.sessionConfigurationWebinarId = null;
      state.sessionConfigurationError = null;
      state.currentWaitingRoomPage = null;
      state.waitingRoomPageError = null;
      state.thankYouPageLoading = false;
      state.currentThankYouPage = null;
      state.thankYouPageError = null;
      state.offerTemplates = [];
      state.offerTemplatesError = null;
      state.currentOfferTemplate = null;
      state.currentOfferTemplateError = null;
      state.surveyTemplates = [];
      state.surveyTemplatesLoading = false;
      state.surveyTemplatesError = null;
      state.currentSurveyTemplate = null;
      state.currentSurveyTemplateLoading = false;
      state.currentSurveyTemplateError = null;
      state.surveyQuestions = [];
      state.surveyQuestionsLoading = false;
      state.surveyQuestionsError = null;
      state.surveyAnswerOptions = [];
      state.surveyAnswerOptionsLoading = false;
      state.surveyAnswerOptionsError = null;
      state.speakers = [];
      state.speakersLoading = false;
      state.speakersError = null;
      state.currentSpeaker = null;
      state.currentSpeakerLoading = false;
      state.currentSpeakerError = null;
      state.moderators = [];
      state.moderatorsLoading = false;
      state.moderatorsError = null;
      state.currentModerator = null;
      state.currentModeratorLoading = false;
      state.currentModeratorError = null;
      state.presenters = [];
      state.presentersLoading = false;
      state.presentersError = null;
      state.currentPresenter = null;
      state.currentPresenterLoading = false;
      state.currentPresenterError = null;
      state.webinarResources = [];
      state.webinarResourcesTotalElements = 0;
      state.webinarResourcesTotalPages = 0;
      state.webinarResourcesLoading = false;
      state.webinarResourcesError = null;
      state.webinarResourceUploadInit = null;
      state.webinarResourceUploadInitLoading = false;
      state.webinarResourceUploadInitError = null;
      state.webinarResourceFinalizeLoading = false;
      state.webinarResourceFinalizeError = null;
      state.quizTemplates = [];
      state.quizTemplatesLoading = false;
      state.quizTemplatesError = null;
      state.currentQuizTemplate = null;
      state.currentQuizTemplateLoading = false;
      state.currentQuizTemplateError = null;
      state.quizQuestions = [];
      state.quizQuestionsLoading = false;
      state.quizQuestionsError = null;
      state.quizAnswerOptions = [];
      state.speakers = [];
      state.speakersLoading = false;
      state.speakersError = null;
      state.currentSpeaker = null;
      state.currentSpeakerLoading = false;
      state.currentSpeakerError = null;
      state.quizAnswerOptionsLoading = false;
      state.quizAnswerOptionsError = null;
    });

    // Webinar Index reducers
    builder.addCase(fetchWebinarIndex.pending, (state) => {
      state.indexLoading = true;
      state.indexError = null;
    });
    builder.addCase(fetchWebinarIndex.fulfilled, (state, action: PayloadAction<WebinarIndex>) => {
      state.indexLoading = false;
      state.currentWebinarIndex = action.payload;
      state.indexError = null;
    });
    builder.addCase(fetchWebinarIndex.rejected, (state, action) => {
      state.indexLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.indexError =
        payload?.message ?? action.error.message ?? 'Failed to fetch webinar index';
    });

    builder.addCase(listIndexItems.pending, (state) => {
      state.indexLoading = true;
      state.indexError = null;
    });
    builder.addCase(listIndexItems.fulfilled, (state, action: PayloadAction<IndexItem[]>) => {
      state.indexLoading = false;
      if (state.currentWebinarIndex) {
        state.currentWebinarIndex.items = action.payload;
      }
      state.indexError = null;
    });
    builder.addCase(listIndexItems.rejected, (state, action) => {
      state.indexLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.indexError = payload?.message ?? action.error.message ?? 'Failed to list index items';
    });

    builder.addCase(createIndexItem.pending, (state) => {
      state.indexLoading = true;
      state.indexError = null;
    });
    builder.addCase(createIndexItem.fulfilled, (state) => {
      state.indexLoading = false;
      state.indexError = null;
    });
    builder.addCase(createIndexItem.rejected, (state, action) => {
      state.indexLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.indexError = payload?.message ?? action.error.message ?? 'Failed to create index item';
    });

    builder.addCase(updateIndexItem.pending, (state) => {
      state.indexLoading = true;
      state.indexError = null;
    });
    builder.addCase(updateIndexItem.fulfilled, (state) => {
      state.indexLoading = false;
      state.indexError = null;
    });
    builder.addCase(updateIndexItem.rejected, (state, action) => {
      state.indexLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.indexError = payload?.message ?? action.error.message ?? 'Failed to update index item';
    });

    builder.addCase(deleteIndexItem.pending, (state) => {
      state.indexLoading = true;
      state.indexError = null;
    });
    builder.addCase(deleteIndexItem.fulfilled, (state) => {
      state.indexLoading = false;
      state.indexError = null;
    });
    builder.addCase(deleteIndexItem.rejected, (state, action) => {
      state.indexLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.indexError = payload?.message ?? action.error.message ?? 'Failed to delete index item';
    });

    builder.addCase(replaceIndexItems.pending, (state) => {
      state.indexLoading = true;
      state.indexError = null;
    });
    builder.addCase(replaceIndexItems.fulfilled, (state) => {
      state.indexLoading = false;
      state.indexError = null;
    });
    builder.addCase(replaceIndexItems.rejected, (state, action) => {
      state.indexLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.indexError =
        payload?.message ?? action.error.message ?? 'Failed to replace index items';
    });

    // Webinar Subscription Page reducers
    builder.addCase(fetchWebinarSubscriptionPage.pending, (state) => {
      state.subscriptionPageLoading = true;
      state.subscriptionPageError = null;
    });
    builder.addCase(
      fetchWebinarSubscriptionPage.fulfilled,
      (state, action: PayloadAction<WebinarSubscriptionPage>) => {
        state.subscriptionPageLoading = false;
        state.currentWebinarSubscriptionPage = action.payload;
        state.subscriptionPageError = null;
      },
    );
    builder.addCase(fetchWebinarSubscriptionPage.rejected, (state, action) => {
      state.subscriptionPageLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.subscriptionPageError =
        payload?.message ?? action.error.message ?? 'Failed to fetch webinar subscription page';
    });

    builder.addCase(updateWebinarSubscriptionPage.pending, (state) => {
      state.subscriptionPageLoading = true;
      state.subscriptionPageError = null;
    });
    builder.addCase(
      updateWebinarSubscriptionPage.fulfilled,
      (state, action: PayloadAction<WebinarSubscriptionPage>) => {
        state.subscriptionPageLoading = false;
        state.currentWebinarSubscriptionPage = action.payload;
        state.subscriptionPageError = null;
      },
    );
    builder.addCase(updateWebinarSubscriptionPage.rejected, (state, action) => {
      state.subscriptionPageLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.subscriptionPageError =
        payload?.message ?? action.error.message ?? 'Failed to update webinar subscription page';
    });

    builder.addCase(uploadWebinarSubscriptionPageBackgroundImage.pending, (state) => {
      state.subscriptionPageLoading = true;
      state.subscriptionPageError = null;
    });
    builder.addCase(
      uploadWebinarSubscriptionPageBackgroundImage.fulfilled,
      (state, action: PayloadAction<WebinarSubscriptionPage>) => {
        state.subscriptionPageLoading = false;
        state.currentWebinarSubscriptionPage = action.payload;
        state.subscriptionPageError = null;
      },
    );
    builder.addCase(uploadWebinarSubscriptionPageBackgroundImage.rejected, (state, action) => {
      state.subscriptionPageLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.subscriptionPageError =
        payload?.message ?? action.error.message ?? 'Failed to upload background image';
    });

    builder.addCase(fetchReplayConfiguration.pending, (state) => {
      state.replayConfigurationLoading = true;
      state.replayConfigurationError = null;
    });
    builder.addCase(
      fetchReplayConfiguration.fulfilled,
      (state, action: PayloadAction<ReplayConfiguration>) => {
        state.replayConfigurationLoading = false;
        state.currentReplayConfiguration = action.payload;
        state.replayConfigurationError = null;
      },
    );
    builder.addCase(fetchReplayConfiguration.rejected, (state, action) => {
      state.replayConfigurationLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.replayConfigurationError =
        payload?.message ?? action.error.message ?? 'Failed to fetch replay configuration';
    });

    builder.addCase(fetchEvergreenConfiguration.pending, (state) => {
      state.evergreenConfigurationLoading = true;
      state.evergreenConfigurationError = null;
    });
    builder.addCase(
      fetchEvergreenConfiguration.fulfilled,
      (state, action: PayloadAction<EvergreenConfiguration>) => {
        state.evergreenConfigurationLoading = false;
        state.currentEvergreenConfiguration = action.payload;
        state.evergreenConfigurationError = null;
      },
    );
    builder.addCase(fetchEvergreenConfiguration.rejected, (state, action) => {
      state.evergreenConfigurationLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.evergreenConfigurationError =
        payload?.message ?? action.error.message ?? 'Failed to fetch evergreen configuration';
    });

    builder.addCase(fetchEvergreenTimeline.pending, (state) => {
      state.evergreenTimelineLoading = true;
      state.evergreenTimelineError = null;
    });
    builder.addCase(
      fetchEvergreenTimeline.fulfilled,
      (state, action: PayloadAction<EvergreenTimelineEvent[]>) => {
        state.evergreenTimelineLoading = false;
        state.evergreenTimeline = action.payload;
        state.evergreenTimelineError = null;
      },
    );
    builder.addCase(fetchEvergreenTimeline.rejected, (state, action) => {
      state.evergreenTimelineLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.evergreenTimelineError =
        payload?.message ?? action.error.message ?? 'Failed to fetch evergreen timeline';
    });

    builder.addCase(replaceEvergreenTimeline.pending, (state) => {
      state.evergreenTimelineLoading = true;
      state.evergreenTimelineError = null;
    });
    builder.addCase(
      replaceEvergreenTimeline.fulfilled,
      (state, action: PayloadAction<EvergreenTimelineEvent[]>) => {
        state.evergreenTimelineLoading = false;
        state.evergreenTimeline = action.payload;
        state.evergreenTimelineError = null;
      },
    );
    builder.addCase(replaceEvergreenTimeline.rejected, (state, action) => {
      state.evergreenTimelineLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.evergreenTimelineError =
        payload?.message ?? action.error.message ?? 'Failed to replace evergreen timeline';
    });

    builder.addCase(deleteEvergreenTimeline.pending, (state) => {
      state.evergreenTimelineLoading = true;
      state.evergreenTimelineError = null;
    });
    builder.addCase(deleteEvergreenTimeline.fulfilled, (state) => {
      state.evergreenTimelineLoading = false;
      state.evergreenTimeline = [];
      state.evergreenTimelineError = null;
    });
    builder.addCase(deleteEvergreenTimeline.rejected, (state, action) => {
      state.evergreenTimelineLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.evergreenTimelineError =
        payload?.message ?? action.error.message ?? 'Failed to delete evergreen timeline';
    });

    builder.addCase(updateReplayConfiguration.pending, (state) => {
      state.replayConfigurationLoading = true;
      state.replayConfigurationError = null;
    });
    builder.addCase(
      updateReplayConfiguration.fulfilled,
      (state, action: PayloadAction<ReplayConfiguration>) => {
        state.replayConfigurationLoading = false;
        state.currentReplayConfiguration = action.payload;
        state.replayConfigurationError = null;
      },
    );
    builder.addCase(updateReplayConfiguration.rejected, (state, action) => {
      state.replayConfigurationLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.replayConfigurationError =
        payload?.message ?? action.error.message ?? 'Failed to update replay configuration';
    });

    builder.addCase(updateEvergreenConfiguration.pending, (state) => {
      state.evergreenConfigurationLoading = true;
      state.evergreenConfigurationError = null;
    });
    builder.addCase(
      updateEvergreenConfiguration.fulfilled,
      (state, action: PayloadAction<EvergreenConfiguration>) => {
        state.evergreenConfigurationLoading = false;
        state.currentEvergreenConfiguration = action.payload;
        state.evergreenConfigurationError = null;
      },
    );
    builder.addCase(updateEvergreenConfiguration.rejected, (state, action) => {
      state.evergreenConfigurationLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.evergreenConfigurationError =
        payload?.message ?? action.error.message ?? 'Failed to update evergreen configuration';
    });

    builder.addCase(saveEvergreenSources.pending, (state) => {
      state.evergreenConvertLoading = true;
      state.evergreenConvertError = null;
    });
    builder.addCase(saveEvergreenSources.fulfilled, (state) => {
      state.evergreenConvertLoading = false;
      state.evergreenConvertError = null;
    });
    builder.addCase(saveEvergreenSources.rejected, (state, action) => {
      state.evergreenConvertLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.evergreenConvertError =
        payload?.message ?? action.error.message ?? 'Failed to save evergreen sources';
    });

    builder.addCase(importEvergreenChat.pending, (state) => {
      state.evergreenChatImportLoading = true;
      state.evergreenChatImportError = null;
    });
    builder.addCase(importEvergreenChat.fulfilled, (state) => {
      state.evergreenChatImportLoading = false;
      state.evergreenChatImportError = null;
    });
    builder.addCase(importEvergreenChat.rejected, (state, action) => {
      state.evergreenChatImportLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.evergreenChatImportError =
        payload?.message ?? action.error.message ?? 'Failed to import evergreen chat';
    });

    builder.addCase(fetchEvergreenChatConfiguration.pending, (state) => {
      state.evergreenChatConfigurationLoading = true;
      state.evergreenChatConfigurationError = null;
    });
    builder.addCase(
      fetchEvergreenChatConfiguration.fulfilled,
      (state, action: PayloadAction<EvergreenChatConfiguration>) => {
        state.evergreenChatConfigurationLoading = false;
        state.currentEvergreenChatConfiguration = action.payload;
        state.evergreenChatConfigurationError = null;
      },
    );
    builder.addCase(fetchEvergreenChatConfiguration.rejected, (state, action) => {
      state.evergreenChatConfigurationLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.evergreenChatConfigurationError =
        payload?.message ?? action.error.message ?? 'Failed to fetch evergreen chat configuration';
    });

    builder.addCase(updateEvergreenChatConfiguration.pending, (state) => {
      state.evergreenChatConfigurationLoading = true;
      state.evergreenChatConfigurationError = null;
    });
    builder.addCase(
      updateEvergreenChatConfiguration.fulfilled,
      (state, action: PayloadAction<EvergreenChatConfiguration>) => {
        state.evergreenChatConfigurationLoading = false;
        state.currentEvergreenChatConfiguration = action.payload;
        state.evergreenChatConfigurationError = null;
      },
    );
    builder.addCase(updateEvergreenChatConfiguration.rejected, (state, action) => {
      state.evergreenChatConfigurationLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.evergreenChatConfigurationError =
        payload?.message ?? action.error.message ?? 'Failed to update evergreen chat configuration';
    });

    builder.addCase(addEvergreenChatMessage.pending, (state) => {
      state.evergreenChatConfigurationLoading = true;
      state.evergreenChatConfigurationError = null;
    });
    builder.addCase(
      addEvergreenChatMessage.fulfilled,
      (state, action: PayloadAction<EvergreenChatConfiguration>) => {
        state.evergreenChatConfigurationLoading = false;
        state.currentEvergreenChatConfiguration = action.payload;
        state.evergreenChatConfigurationError = null;
      },
    );
    builder.addCase(addEvergreenChatMessage.rejected, (state, action) => {
      state.evergreenChatConfigurationLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.evergreenChatConfigurationError =
        payload?.message ?? action.error.message ?? 'Failed to add evergreen chat message';
    });

    builder.addCase(updateEvergreenChatMessage.pending, (state) => {
      state.evergreenChatConfigurationLoading = true;
      state.evergreenChatConfigurationError = null;
    });
    builder.addCase(
      updateEvergreenChatMessage.fulfilled,
      (state, action: PayloadAction<EvergreenChatConfiguration>) => {
        state.evergreenChatConfigurationLoading = false;
        state.currentEvergreenChatConfiguration = action.payload;
        state.evergreenChatConfigurationError = null;
      },
    );
    builder.addCase(updateEvergreenChatMessage.rejected, (state, action) => {
      state.evergreenChatConfigurationLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.evergreenChatConfigurationError =
        payload?.message ?? action.error.message ?? 'Failed to update evergreen chat message';
    });

    builder.addCase(deleteEvergreenChatMessages.pending, (state) => {
      state.evergreenChatConfigurationLoading = true;
      state.evergreenChatConfigurationError = null;
    });
    builder.addCase(
      deleteEvergreenChatMessages.fulfilled,
      (state, action: PayloadAction<EvergreenChatConfiguration>) => {
        state.evergreenChatConfigurationLoading = false;
        state.currentEvergreenChatConfiguration = action.payload;
        state.evergreenChatConfigurationError = null;
      },
    );
    builder.addCase(deleteEvergreenChatMessages.rejected, (state, action) => {
      state.evergreenChatConfigurationLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.evergreenChatConfigurationError =
        payload?.message ?? action.error.message ?? 'Failed to delete evergreen chat messages';
    });

    // Webinar session configuration reducers
    builder.addCase(fetchWebinarSessionConfiguration.pending, (state, action) => {
      state.sessionConfigurationLoading = true;
      state.sessionConfigurationError = null;
      state.sessionConfigurationWebinarId = action.meta.arg.webinarId;
      state.currentSessionConfiguration = null;
    });
    builder.addCase(fetchWebinarSessionConfiguration.fulfilled, (state, action) => {
      if (action.meta.arg.webinarId !== state.sessionConfigurationWebinarId) {
        return;
      }
      state.sessionConfigurationLoading = false;
      state.currentSessionConfiguration = action.payload;
      state.sessionConfigurationError = null;
    });
    builder.addCase(fetchWebinarSessionConfiguration.rejected, (state, action) => {
      if (action.meta.arg.webinarId !== state.sessionConfigurationWebinarId) {
        return;
      }
      state.sessionConfigurationLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.sessionConfigurationError =
        payload?.message ?? action.error.message ?? 'Failed to fetch session configuration';
    });

    builder.addCase(updateWebinarSessionConfiguration.pending, (state, action) => {
      state.sessionConfigurationLoading = true;
      state.sessionConfigurationError = null;
      state.sessionConfigurationWebinarId = action.meta.arg.webinarId;
    });
    builder.addCase(updateWebinarSessionConfiguration.fulfilled, (state, action) => {
      if (action.meta.arg.webinarId !== state.sessionConfigurationWebinarId) {
        return;
      }
      state.sessionConfigurationLoading = false;
      state.currentSessionConfiguration = action.payload;
      state.sessionConfigurationError = null;
    });
    builder.addCase(updateWebinarSessionConfiguration.rejected, (state, action) => {
      state.sessionConfigurationLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.sessionConfigurationError =
        payload?.message ?? action.error.message ?? 'Failed to update session configuration';
    });

    builder.addCase(fetchPublicWebinarSession.pending, (state, action) => {
      state.publicSessionLoading = true;
      state.publicSessionError = null;
      state.publicSessionErrorTransient = false;
      state.publicSessionRequestId = action.meta.requestId;
      state.publicSessionKey = publicSessionReadKey(action.meta.arg);
    });
    builder.addCase(fetchPublicSession.pending, (state, action) => {
      state.publicSessionKey = publicSessionReadKey(action.meta.arg);
    });
    // Une relecture plus récente, ou d'une autre session, est partie entre-temps : c'est elle qui
    // tranche, en succès comme en échec.
    const isSupersededRead = (
      state: WebinarState,
      meta: { requestId: string; arg: { webinarId: number; sessionIdentifier: string } },
    ) =>
      !isForActivePublicSession(state.publicSessionKey, meta.arg) ||
      (state.publicSessionRequestId != null && meta.requestId !== state.publicSessionRequestId);
    builder.addCase(fetchPublicWebinarSession.fulfilled, (state, action) => {
      if (isSupersededRead(state, action.meta)) return;
      state.publicSessionLoading = false;
      if (isPublicSessionApplicable(state.currentPublicSession, action.payload)) {
        state.currentPublicSession = action.payload;
      }
      state.publicSessionError = null;
      state.publicSessionErrorTransient = false;
    });
    builder.addCase(fetchPublicWebinarSession.rejected, (state, action) => {
      if (isSupersededRead(state, action.meta)) return;
      state.publicSessionLoading = false;
      const payload = action.payload as { message?: string; status?: number } | undefined;
      state.publicSessionError =
        payload?.message ?? action.error.message ?? 'Failed to fetch public webinar session';
      state.publicSessionErrorTransient = isTransientPublicReadFailure(payload);
    });

    // Miroir de fetchPublicSession : les deux thunks visent le même endpoint, les deux copies
    // doivent avancer ensemble pour qu'aucune page ne décide sur un statut plus vieux que l'autre.
    builder.addCase(fetchPublicSession.fulfilled, (state, action) => {
      // Miroir seul : on n'a pas déclenché ce thunk, on ne touche donc ni l'erreur ni le
      // drapeau de chargement de ce slice, qui appartiennent à son propre cycle.
      if (!isForActivePublicSession(state.publicSessionKey, action.meta.arg)) return;
      if (!isPublicSessionApplicable(state.currentPublicSession, action.payload)) return;
      state.currentPublicSession = action.payload;
    });

    builder.addCase(fetchPublicWebinarSubscriptionForm.pending, (state) => {
      state.publicSubscriptionFormLoading = true;
      state.publicSubscriptionFormError = null;
    });
    builder.addCase(
      fetchPublicWebinarSubscriptionForm.fulfilled,
      (state, action: PayloadAction<PublicWebinarSubscriptionForm>) => {
        state.publicSubscriptionFormLoading = false;
        state.currentPublicSubscriptionForm = action.payload;
        state.publicSubscriptionFormError = null;
      },
    );
    builder.addCase(fetchPublicWebinarSubscriptionForm.rejected, (state, action) => {
      state.publicSubscriptionFormLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.publicSubscriptionFormError =
        payload?.message ??
        action.error.message ??
        'Failed to fetch public webinar subscription form';
    });

    builder.addCase(fetchPublicWebinarIndex.pending, (state) => {
      state.publicWebinarIndexLoading = true;
      state.publicWebinarIndexError = null;
    });
    builder.addCase(
      fetchPublicWebinarIndex.fulfilled,
      (state, action: PayloadAction<WebinarIndex>) => {
        state.publicWebinarIndexLoading = false;
        state.currentPublicWebinarIndex = action.payload;
        state.publicWebinarIndexError = null;
      },
    );
    builder.addCase(fetchPublicWebinarIndex.rejected, (state, action) => {
      state.publicWebinarIndexLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.publicWebinarIndexError =
        payload?.message ?? action.error.message ?? 'Failed to fetch public webinar index';
    });

    builder.addCase(fetchPublicWebinarMe.pending, (state) => {
      state.publicWebinarMeLoading = true;
      state.publicWebinarMeError = null;
    });
    builder.addCase(
      fetchPublicWebinarMe.fulfilled,
      (state, action: PayloadAction<PublicWebinarMe>) => {
        state.publicWebinarMeLoading = false;
        state.currentPublicWebinarMe = action.payload;
        state.publicWebinarMeError = null;
      },
    );
    builder.addCase(fetchPublicWebinarMe.rejected, (state, action) => {
      state.publicWebinarMeLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.publicWebinarMeError =
        payload?.message ?? action.error.message ?? 'Failed to fetch public webinar me';
    });

    builder.addCase(fetchPublicWaitingRoomSurvey.pending, (state) => {
      state.publicWaitingRoomSurveyLoading = true;
      state.publicWaitingRoomSurveyError = null;
    });
    builder.addCase(
      fetchPublicWaitingRoomSurvey.fulfilled,
      (state, action: PayloadAction<SurveyTemplate>) => {
        state.publicWaitingRoomSurveyLoading = false;
        state.currentPublicWaitingRoomSurvey = action.payload;
        state.publicWaitingRoomSurveyError = null;
      },
    );
    builder.addCase(fetchPublicWaitingRoomSurvey.rejected, (state, action) => {
      state.publicWaitingRoomSurveyLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.publicWaitingRoomSurveyError =
        payload?.message ?? action.error.message ?? 'Failed to fetch public waiting room survey';
    });

    builder.addCase(fetchPublicWaitingRoomQuiz.pending, (state) => {
      state.publicWaitingRoomQuizLoading = true;
      state.publicWaitingRoomQuizError = null;
    });
    builder.addCase(
      fetchPublicWaitingRoomQuiz.fulfilled,
      (state, action: PayloadAction<QuizTemplate>) => {
        state.publicWaitingRoomQuizLoading = false;
        state.currentPublicWaitingRoomQuiz = action.payload;
        state.publicWaitingRoomQuizError = null;
      },
    );
    builder.addCase(fetchPublicWaitingRoomQuiz.rejected, (state, action) => {
      state.publicWaitingRoomQuizLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.publicWaitingRoomQuizError =
        payload?.message ?? action.error.message ?? 'Failed to fetch public waiting room quiz';
    });

    builder.addCase(submitPublicWaitingRoomSurvey.pending, (state) => {
      state.publicWaitingRoomSurveySubmitLoading = true;
      state.publicWaitingRoomSurveySubmitError = null;
    });
    builder.addCase(submitPublicWaitingRoomSurvey.fulfilled, (state) => {
      state.publicWaitingRoomSurveySubmitLoading = false;
      state.publicWaitingRoomSurveySubmitError = null;
    });
    builder.addCase(submitPublicWaitingRoomSurvey.rejected, (state, action) => {
      state.publicWaitingRoomSurveySubmitLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.publicWaitingRoomSurveySubmitError =
        payload?.message ?? action.error.message ?? 'Failed to submit waiting room survey';
    });

    builder.addCase(fetchPublicWaitingRoomPage.pending, (state) => {
      state.publicWaitingRoomPageLoading = true;
      state.publicWaitingRoomPageError = null;
    });
    builder.addCase(
      fetchPublicWaitingRoomPage.fulfilled,
      (state, action: PayloadAction<WaitingRoomPage>) => {
        state.publicWaitingRoomPageLoading = false;
        state.currentPublicWaitingRoomPage = action.payload;
        state.publicWaitingRoomPageError = null;
      },
    );
    builder.addCase(fetchPublicWaitingRoomPage.rejected, (state, action) => {
      state.publicWaitingRoomPageLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.publicWaitingRoomPageError =
        payload?.message ?? action.error.message ?? 'Failed to fetch public waiting room page';
    });

    builder.addCase(registerPublicWebinar.pending, (state) => {
      state.publicWebinarRegisterLoading = true;
      state.publicWebinarRegisterError = null;
    });
    builder.addCase(registerPublicWebinar.fulfilled, (state) => {
      state.publicWebinarRegisterLoading = false;
      state.publicWebinarRegisterError = null;
    });
    builder.addCase(registerPublicWebinar.rejected, (state, action) => {
      state.publicWebinarRegisterLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.publicWebinarRegisterError =
        payload?.message ?? action.error.message ?? 'Failed to register for webinar';
    });

    builder.addCase(registerPublicWebinarReplay.pending, (state) => {
      state.publicWebinarRegisterLoading = true;
      state.publicWebinarRegisterError = null;
    });
    builder.addCase(registerPublicWebinarReplay.fulfilled, (state) => {
      state.publicWebinarRegisterLoading = false;
      state.publicWebinarRegisterError = null;
    });
    builder.addCase(registerPublicWebinarReplay.rejected, (state, action) => {
      state.publicWebinarRegisterLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.publicWebinarRegisterError =
        payload?.message ?? action.error.message ?? 'Failed to register for webinar replay';
    });

    builder.addCase(submitPublicSurvey.pending, (state) => {
      state.publicSurveySubmitLoading = true;
      state.publicSurveySubmitError = null;
    });
    builder.addCase(submitPublicSurvey.fulfilled, (state) => {
      state.publicSurveySubmitLoading = false;
      state.publicSurveySubmitError = null;
    });
    builder.addCase(submitPublicSurvey.rejected, (state, action) => {
      state.publicSurveySubmitLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.publicSurveySubmitError =
        payload?.message ?? action.error.message ?? 'Failed to submit survey';
    });

    builder.addCase(submitPublicSurveyPut.pending, (state) => {
      state.publicSurveySubmitLoading = true;
      state.publicSurveySubmitError = null;
    });
    builder.addCase(submitPublicSurveyPut.fulfilled, (state) => {
      state.publicSurveySubmitLoading = false;
      state.publicSurveySubmitError = null;
    });
    builder.addCase(submitPublicSurveyPut.rejected, (state, action) => {
      state.publicSurveySubmitLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.publicSurveySubmitError =
        payload?.message ?? action.error.message ?? 'Failed to update survey';
    });

    builder.addCase(submitPublicQuiz.pending, (state) => {
      state.publicQuizSubmitLoading = true;
      state.publicQuizSubmitError = null;
    });
    builder.addCase(submitPublicQuiz.fulfilled, (state) => {
      state.publicQuizSubmitLoading = false;
      state.publicQuizSubmitError = null;
    });
    builder.addCase(submitPublicQuiz.rejected, (state, action) => {
      state.publicQuizSubmitLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.publicQuizSubmitError =
        payload?.message ?? action.error.message ?? 'Failed to submit quiz';
    });

    builder.addCase(submitPublicQuizPut.pending, (state) => {
      state.publicQuizSubmitLoading = true;
      state.publicQuizSubmitError = null;
    });
    builder.addCase(submitPublicQuizPut.fulfilled, (state) => {
      state.publicQuizSubmitLoading = false;
      state.publicQuizSubmitError = null;
    });
    builder.addCase(submitPublicQuizPut.rejected, (state, action) => {
      state.publicQuizSubmitLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.publicQuizSubmitError =
        payload?.message ?? action.error.message ?? 'Failed to submit quiz';
    });

    builder.addCase(fetchMyPublicSurveySubmission.pending, (state) => {
      state.publicSurveySubmissionLoading = true;
      state.publicSurveySubmissionError = null;
    });
    builder.addCase(
      fetchMyPublicSurveySubmission.fulfilled,
      (state, action: PayloadAction<PublicSurveySubmission | null>) => {
        state.publicSurveySubmissionLoading = false;
        state.currentPublicSurveySubmission = action.payload;
        state.publicSurveySubmissionError = null;
      },
    );
    builder.addCase(fetchMyPublicSurveySubmission.rejected, (state, action) => {
      state.publicSurveySubmissionLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.publicSurveySubmissionError =
        payload?.message ?? action.error.message ?? 'Failed to fetch public survey submission';
    });

    builder.addCase(fetchMyPublicQuizSubmission.pending, (state) => {
      state.publicQuizSubmissionLoading = true;
      state.publicQuizSubmissionError = null;
    });
    builder.addCase(
      fetchMyPublicQuizSubmission.fulfilled,
      (state, action: PayloadAction<QuizSubmission | null>) => {
        state.publicQuizSubmissionLoading = false;
        state.currentPublicQuizSubmission = action.payload;
        state.publicQuizSubmissionError = null;
      },
    );
    builder.addCase(fetchMyPublicQuizSubmission.rejected, (state, action) => {
      state.publicQuizSubmissionLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.publicQuizSubmissionError =
        payload?.message ?? action.error.message ?? 'Failed to fetch public quiz submission';
    });

    builder.addCase(listWebinarSessions.pending, (state) => {
      state.webinarSessionsLoading = true;
      state.webinarSessionsError = null;
    });
    builder.addCase(
      listWebinarSessions.fulfilled,
      (state, action: PayloadAction<WebinarSession[]>) => {
        state.webinarSessionsLoading = false;
        state.webinarSessions = action.payload;
        state.webinarSessionsError = null;
      },
    );
    builder.addCase(listWebinarSessions.rejected, (state, action) => {
      state.webinarSessionsLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.webinarSessionsError =
        payload?.message ?? action.error.message ?? 'Failed to list webinar sessions';
    });

    builder.addCase(getWebinarSession.pending, (state) => {
      state.currentWebinarSessionLoading = true;
      state.currentWebinarSessionError = null;
    });
    builder.addCase(getWebinarSession.fulfilled, (state, action: PayloadAction<WebinarSession>) => {
      state.currentWebinarSessionLoading = false;
      state.currentWebinarSession = action.payload;
      state.currentWebinarSessionError = null;
    });
    builder.addCase(getWebinarSession.rejected, (state, action) => {
      state.currentWebinarSessionLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.currentWebinarSessionError =
        payload?.message ?? action.error.message ?? 'Failed to get webinar session';
    });

    builder.addCase(fetchWebinarSessionDetail.pending, (state) => {
      state.sessionDetailsLoading = true;
      state.sessionDetailsError = null;
    });
    builder.addCase(
      fetchWebinarSessionDetail.fulfilled,
      (state, action: PayloadAction<WebinarSessionDetail>) => {
        state.sessionDetailsLoading = false;
        if (!state.sessionDetailsBySessionId) state.sessionDetailsBySessionId = {};
        state.sessionDetailsBySessionId[action.payload.id] = action.payload;
        state.sessionDetailsError = null;
      },
    );
    builder.addCase(fetchWebinarSessionDetail.rejected, (state, action) => {
      state.sessionDetailsLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.sessionDetailsError =
        payload?.message ?? action.error.message ?? 'Failed to get session detail';
    });

    builder.addCase(fetchSessionChatExport.pending, (state) => {
      state.sessionChatExportLoading = true;
      state.sessionChatExportError = null;
    });
    builder.addCase(fetchSessionChatExport.fulfilled, (state) => {
      state.sessionChatExportLoading = false;
      state.sessionChatExportError = null;
    });
    builder.addCase(fetchSessionChatExport.rejected, (state, action) => {
      state.sessionChatExportLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.sessionChatExportError =
        payload?.message ?? action.error.message ?? 'Failed to export session chat';
    });

    builder.addCase(listSessionAttendees.pending, (state) => {
      state.sessionAttendeesLoading = true;
      state.sessionAttendeesError = null;
    });
    builder.addCase(
      listSessionAttendees.fulfilled,
      (
        state,
        action: PayloadAction<{
          content: WebinarSessionAttendee[];
          totalElements: number;
          totalPages: number;
        }>,
      ) => {
        state.sessionAttendeesLoading = false;
        state.sessionAttendees = action.payload.content;
        state.sessionAttendeesTotalElements = action.payload.totalElements;
        state.sessionAttendeesTotalPages = action.payload.totalPages;
        state.sessionAttendeesError = null;
      },
    );
    builder.addCase(listSessionAttendees.rejected, (state, action) => {
      state.sessionAttendeesLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.sessionAttendeesError =
        payload?.message ?? action.error.message ?? 'Failed to list session attendees';
    });

    builder.addCase(deleteSessionAttendee.fulfilled, (state, action) => {
      // Retrait immédiat de la ligne : évite un aller-retour réseau et laisse le compteur
      // total cohérent avec la liste affichée en attendant le prochain refetch de la page.
      state.sessionAttendees = (state.sessionAttendees ?? []).filter(
        (attendee) => attendee.subscriptionId !== action.payload.subscriptionId,
      );
      state.sessionAttendeesTotalElements = Math.max(
        0,
        (state.sessionAttendeesTotalElements ?? 0) - 1,
      );
    });

    builder.addCase(exportSessionAttendees.pending, (state) => {
      state.sessionAttendeesExport.loading = true;
      state.sessionAttendeesExport.error = null;
    });
    builder.addCase(exportSessionAttendees.fulfilled, (state) => {
      state.sessionAttendeesExport.loading = false;
      state.sessionAttendeesExport.error = null;
    });
    builder.addCase(exportSessionAttendees.rejected, (state, action) => {
      state.sessionAttendeesExport.loading = false;
      state.sessionAttendeesExport.error =
        (action.payload as string) || action.error.message || 'FAILED_TO_EXPORT_SESSION_ATTENDEES';
    });

    builder.addCase(importSessionAttendees.pending, (state) => {
      state.sessionAttendeesImport.loading = true;
      state.sessionAttendeesImport.error = null;
      state.sessionAttendeesImport.result = null;
    });
    builder.addCase(importSessionAttendees.fulfilled, (state, action) => {
      state.sessionAttendeesImport.loading = false;
      state.sessionAttendeesImport.error = null;
      state.sessionAttendeesImport.result = action.payload;
    });
    builder.addCase(importSessionAttendees.rejected, (state, action) => {
      state.sessionAttendeesImport.loading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.sessionAttendeesImport.error =
        payload?.message ?? action.error.message ?? 'FAILED_TO_IMPORT_SESSION_ATTENDEES';
    });

    // Waiting Room Page reducers
    builder.addCase(fetchWaitingRoomPage.pending, (state) => {
      state.waitingRoomPageLoading = true;
      state.waitingRoomPageError = null;
    });
    builder.addCase(
      fetchWaitingRoomPage.fulfilled,
      (state, action: PayloadAction<WaitingRoomPage>) => {
        state.waitingRoomPageLoading = false;
        state.currentWaitingRoomPage = action.payload;
        state.waitingRoomPageError = null;
      },
    );
    builder.addCase(fetchWaitingRoomPage.rejected, (state, action) => {
      state.waitingRoomPageLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.waitingRoomPageError =
        payload?.message ?? action.error.message ?? 'Failed to fetch waiting room page';
    });

    builder.addCase(updateWaitingRoomPage.pending, (state) => {
      state.waitingRoomPageLoading = false;
      state.waitingRoomPageError = null;
    });
    builder.addCase(
      updateWaitingRoomPage.fulfilled,
      (state, action: PayloadAction<WaitingRoomPage>) => {
        state.waitingRoomPageLoading = false;
        state.currentWaitingRoomPage = action.payload;
        state.waitingRoomPageError = null;
      },
    );
    builder.addCase(updateWaitingRoomPage.rejected, (state, action) => {
      state.waitingRoomPageLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.waitingRoomPageError =
        payload?.message ?? action.error.message ?? 'Failed to update waiting room page';
    });

    builder.addCase(uploadWaitingRoomPageBackgroundImage.pending, (state) => {
      state.waitingRoomPageLoading = true;
      state.waitingRoomPageError = null;
    });
    builder.addCase(
      uploadWaitingRoomPageBackgroundImage.fulfilled,
      (state, action: PayloadAction<WaitingRoomPage>) => {
        state.waitingRoomPageLoading = false;
        state.currentWaitingRoomPage = action.payload;
        state.waitingRoomPageError = null;
      },
    );
    builder.addCase(uploadWaitingRoomPageBackgroundImage.rejected, (state, action) => {
      state.waitingRoomPageLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.waitingRoomPageError =
        payload?.message ?? action.error.message ?? 'Failed to upload background image';
    });

    builder.addCase(uploadWaitingRoomPageImage.pending, (state) => {
      state.waitingRoomPageLoading = true;
      state.waitingRoomPageError = null;
    });
    builder.addCase(
      uploadWaitingRoomPageImage.fulfilled,
      (state, action: PayloadAction<WaitingRoomPage>) => {
        state.waitingRoomPageLoading = false;
        state.currentWaitingRoomPage = action.payload;
        state.waitingRoomPageError = null;
      },
    );
    builder.addCase(uploadWaitingRoomPageImage.rejected, (state, action) => {
      state.waitingRoomPageLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.waitingRoomPageError =
        payload?.message ?? action.error.message ?? 'Failed to upload image';
    });

    builder.addCase(uploadWaitingRoomPageVideo.pending, (state) => {
      state.waitingRoomPageLoading = true;
      state.waitingRoomPageError = null;
    });
    builder.addCase(
      uploadWaitingRoomPageVideo.fulfilled,
      (state, action: PayloadAction<WaitingRoomPage>) => {
        state.waitingRoomPageLoading = false;
        state.currentWaitingRoomPage = action.payload;
        state.waitingRoomPageError = null;
      },
    );
    builder.addCase(uploadWaitingRoomPageVideo.rejected, (state, action) => {
      state.waitingRoomPageLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.waitingRoomPageError =
        payload?.message ?? action.error.message ?? 'Failed to upload waiting room video';
    });

    builder.addCase(deleteWaitingRoomPageMainAsset.pending, (state) => {
      state.waitingRoomPageLoading = true;
      state.waitingRoomPageError = null;
    });
    builder.addCase(
      deleteWaitingRoomPageMainAsset.fulfilled,
      (state, action: PayloadAction<WaitingRoomPage>) => {
        state.waitingRoomPageLoading = false;
        state.currentWaitingRoomPage = action.payload;
        state.waitingRoomPageError = null;
      },
    );
    builder.addCase(deleteWaitingRoomPageMainAsset.rejected, (state, action) => {
      state.waitingRoomPageLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.waitingRoomPageError =
        payload?.message ?? action.error.message ?? 'Failed to delete main media';
    });

    // Thank You Page reducers
    builder.addCase(fetchThankYouPage.pending, (state) => {
      state.thankYouPageLoading = true;
      state.thankYouPageError = null;
    });
    builder.addCase(fetchThankYouPage.fulfilled, (state, action: PayloadAction<ThankYouPage>) => {
      state.thankYouPageLoading = false;
      state.currentThankYouPage = action.payload;
      state.thankYouPageError = null;
    });
    builder.addCase(fetchThankYouPage.rejected, (state, action) => {
      state.thankYouPageLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.thankYouPageError =
        payload?.message ?? action.error.message ?? 'Failed to fetch thank you page';
    });

    builder.addCase(updateThankYouPage.pending, (state) => {
      state.thankYouPageLoading = false;
      state.thankYouPageError = null;
    });
    builder.addCase(updateThankYouPage.fulfilled, (state, action: PayloadAction<ThankYouPage>) => {
      state.thankYouPageLoading = false;
      state.currentThankYouPage = action.payload;
      state.thankYouPageError = null;
    });
    builder.addCase(updateThankYouPage.rejected, (state, action) => {
      state.thankYouPageLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.thankYouPageError =
        payload?.message ?? action.error.message ?? 'Failed to update thank you page';
    });

    builder.addCase(uploadThankYouPageImage.pending, (state) => {
      state.thankYouPageLoading = false;
      state.thankYouPageError = null;
    });
    builder.addCase(
      uploadThankYouPageImage.fulfilled,
      (state, action: PayloadAction<ThankYouPage>) => {
        state.thankYouPageLoading = false;
        state.currentThankYouPage = action.payload;
        state.thankYouPageError = null;
      },
    );
    builder.addCase(uploadThankYouPageImage.rejected, (state, action) => {
      state.thankYouPageLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.thankYouPageError =
        payload?.message ?? action.error.message ?? 'Failed to upload thank you image';
    });

    builder.addCase(deleteThankYouPageImage.pending, (state) => {
      state.thankYouPageLoading = false;
      state.thankYouPageError = null;
    });
    builder.addCase(
      deleteThankYouPageImage.fulfilled,
      (state, action: PayloadAction<ThankYouPage>) => {
        state.thankYouPageLoading = false;
        state.currentThankYouPage = action.payload;
        state.thankYouPageError = null;
      },
    );
    builder.addCase(deleteThankYouPageImage.rejected, (state, action) => {
      state.thankYouPageLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.thankYouPageError =
        payload?.message ?? action.error.message ?? 'Failed to delete thank you image';
    });

    // Offer Template reducers
    builder.addCase(listOfferTemplates.pending, (state) => {
      state.offerTemplatesLoading = true;
      state.offerTemplatesError = null;
    });
    builder.addCase(
      listOfferTemplates.fulfilled,
      (state, action: PayloadAction<OfferTemplate[]>) => {
        state.offerTemplatesLoading = false;
        state.offerTemplates = action.payload;
        state.offerTemplatesError = null;
      },
    );
    builder.addCase(listOfferTemplates.rejected, (state, action) => {
      state.offerTemplatesLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.offerTemplatesError =
        payload?.message ?? action.error.message ?? 'Failed to list offer templates';
    });

    builder.addCase(fetchOfferTemplate.pending, (state) => {
      state.currentOfferTemplateLoading = true;
      state.currentOfferTemplateError = null;
    });
    builder.addCase(fetchOfferTemplate.fulfilled, (state, action: PayloadAction<OfferTemplate>) => {
      state.currentOfferTemplateLoading = false;
      state.currentOfferTemplate = action.payload;
      state.currentOfferTemplateError = null;

      const index = state.offerTemplates.findIndex((o) => o.id === action.payload.id);
      if (index >= 0) {
        state.offerTemplates[index] = action.payload;
      } else {
        state.offerTemplates.push(action.payload);
      }
    });
    builder.addCase(fetchOfferTemplate.rejected, (state, action) => {
      state.currentOfferTemplateLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.currentOfferTemplateError =
        payload?.message ?? action.error.message ?? 'Failed to fetch offer template';
    });

    builder.addCase(createOfferTemplate.pending, (state) => {
      state.offerTemplatesLoading = true;
      state.offerTemplatesError = null;
    });
    builder.addCase(createOfferTemplate.fulfilled, (state) => {
      state.offerTemplatesLoading = false;
      state.offerTemplatesError = null;
    });
    builder.addCase(createOfferTemplate.rejected, (state, action) => {
      state.offerTemplatesLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.offerTemplatesError =
        payload?.message ?? action.error.message ?? 'Failed to create offer template';
    });

    builder.addCase(updateOfferTemplate.pending, (state) => {
      state.currentOfferTemplateLoading = true;
      state.currentOfferTemplateError = null;
    });
    builder.addCase(
      updateOfferTemplate.fulfilled,
      (state, action: PayloadAction<OfferTemplate>) => {
        state.currentOfferTemplateLoading = false;
        state.currentOfferTemplateError = null;

        const index = state.offerTemplates.findIndex((o) => o.id === action.payload.id);
        if (index >= 0) {
          state.offerTemplates[index] = action.payload;
        }

        if (state.currentOfferTemplate?.id === action.payload.id) {
          state.currentOfferTemplate = action.payload;
        }
      },
    );
    builder.addCase(updateOfferTemplate.rejected, (state, action) => {
      state.currentOfferTemplateLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.currentOfferTemplateError =
        payload?.message ?? action.error.message ?? 'Failed to update offer template';
    });

    builder.addCase(deleteOfferTemplate.pending, (state) => {
      state.offerTemplatesLoading = true;
      state.offerTemplatesError = null;
    });
    builder.addCase(deleteOfferTemplate.fulfilled, (state, action: PayloadAction<number>) => {
      state.offerTemplatesLoading = false;
      state.offerTemplatesError = null;
      state.offerTemplates = state.offerTemplates.filter((o) => o.id !== action.payload);
      if (state.currentOfferTemplate?.id === action.payload) {
        state.currentOfferTemplate = null;
      }
    });
    builder.addCase(deleteOfferTemplate.rejected, (state, action) => {
      state.offerTemplatesLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.offerTemplatesError =
        payload?.message ?? action.error.message ?? 'Failed to delete offer template';
    });

    builder.addCase(upsertOfferTemplateContent.pending, (state) => {
      state.currentOfferTemplateLoading = true;
      state.currentOfferTemplateError = null;
    });
    builder.addCase(
      upsertOfferTemplateContent.fulfilled,
      (state, action: PayloadAction<OfferTemplate>) => {
        state.currentOfferTemplateLoading = false;
        state.currentOfferTemplateError = null;

        const index = state.offerTemplates.findIndex((o) => o.id === action.payload.id);
        if (index >= 0) {
          state.offerTemplates[index] = action.payload;
        }

        if (state.currentOfferTemplate?.id === action.payload.id) {
          state.currentOfferTemplate = action.payload;
        }
      },
    );
    builder.addCase(upsertOfferTemplateContent.rejected, (state, action) => {
      state.currentOfferTemplateLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.currentOfferTemplateError =
        payload?.message ?? action.error.message ?? 'Failed to upsert offer template content';
    });

    builder.addCase(deleteOfferTemplateContent.pending, (state) => {
      state.currentOfferTemplateLoading = true;
      state.currentOfferTemplateError = null;
    });
    builder.addCase(
      deleteOfferTemplateContent.fulfilled,
      (state, action: PayloadAction<OfferTemplate>) => {
        state.currentOfferTemplateLoading = false;
        state.currentOfferTemplateError = null;

        const index = state.offerTemplates.findIndex((o) => o.id === action.payload.id);
        if (index >= 0) {
          state.offerTemplates[index] = action.payload;
        }

        if (state.currentOfferTemplate?.id === action.payload.id) {
          state.currentOfferTemplate = action.payload;
        }
      },
    );
    builder.addCase(deleteOfferTemplateContent.rejected, (state, action) => {
      state.currentOfferTemplateLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.currentOfferTemplateError =
        payload?.message ?? action.error.message ?? 'Failed to delete offer template content';
    });

    builder.addCase(uploadOfferTemplateContentImage.pending, (state) => {
      state.currentOfferTemplateLoading = true;
      state.currentOfferTemplateError = null;
    });
    builder.addCase(deleteOfferTemplateContentImage.pending, (state) => {
      state.currentOfferTemplateLoading = true;
      state.currentOfferTemplateError = null;
    });
    builder.addCase(
      uploadOfferTemplateContentImage.fulfilled,
      (state, action: PayloadAction<OfferTemplate>) => {
        state.currentOfferTemplateLoading = false;
        state.currentOfferTemplateError = null;

        const index = state.offerTemplates.findIndex((o) => o.id === action.payload.id);
        if (index >= 0) {
          state.offerTemplates[index] = action.payload;
        }

        if (state.currentOfferTemplate?.id === action.payload.id) {
          state.currentOfferTemplate = action.payload;
        }
      },
    );
    builder.addCase(
      deleteOfferTemplateContentImage.fulfilled,
      (state, action: PayloadAction<OfferTemplate>) => {
        state.currentOfferTemplateLoading = false;
        state.currentOfferTemplateError = null;

        const index = state.offerTemplates.findIndex((o) => o.id === action.payload.id);
        if (index >= 0) {
          state.offerTemplates[index] = action.payload;
        }

        if (state.currentOfferTemplate?.id === action.payload.id) {
          state.currentOfferTemplate = action.payload;
        }
      },
    );
    builder.addCase(uploadOfferTemplateContentImage.rejected, (state, action) => {
      state.currentOfferTemplateLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.currentOfferTemplateError =
        payload?.message ?? action.error.message ?? 'Failed to upload offer template content image';
    });
    builder.addCase(deleteOfferTemplateContentImage.rejected, (state, action) => {
      state.currentOfferTemplateLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.currentOfferTemplateError =
        payload?.message ?? action.error.message ?? 'Failed to delete offer template content image';
    });

    // Quit Page reducers
    builder.addCase(fetchQuitPage.pending, (state) => {
      state.quitPageLoading = true;
      state.quitPageError = null;
    });
    builder.addCase(fetchQuitPage.fulfilled, (state, action: PayloadAction<QuitPage>) => {
      state.quitPageLoading = false;
      state.currentQuitPage = action.payload;
      state.quitPageError = null;
    });
    builder.addCase(fetchQuitPage.rejected, (state, action) => {
      state.quitPageLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.quitPageError = payload?.message ?? action.error.message ?? 'Failed to fetch quit page';
    });

    builder.addCase(upsertQuitPage.pending, (state) => {
      state.quitPageLoading = false;
      state.quitPageError = null;
    });
    builder.addCase(upsertQuitPage.fulfilled, (state, action: PayloadAction<QuitPage>) => {
      state.quitPageLoading = false;
      state.currentQuitPage = action.payload;
      state.quitPageError = null;
    });
    builder.addCase(upsertQuitPage.rejected, (state, action) => {
      state.quitPageLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.quitPageError =
        payload?.message ?? action.error.message ?? 'Failed to upsert quit page';
    });

    builder.addCase(listQuitPageReasons.pending, (state) => {
      state.quitPageLoading = false;
      state.quitPageError = null;
    });
    builder.addCase(listQuitPageReasons.fulfilled, (state) => {
      state.quitPageLoading = false;
      state.quitPageError = null;
    });
    builder.addCase(listQuitPageReasons.rejected, (state, action) => {
      state.quitPageLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.quitPageError =
        payload?.message ?? action.error.message ?? 'Failed to list quit page reasons';
    });

    builder.addCase(createQuitPageReason.pending, (state) => {
      state.quitPageLoading = false;
      state.quitPageError = null;
    });
    builder.addCase(createQuitPageReason.fulfilled, (state) => {
      state.quitPageLoading = false;
      state.quitPageError = null;
    });
    builder.addCase(createQuitPageReason.rejected, (state, action) => {
      state.quitPageLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.quitPageError =
        payload?.message ?? action.error.message ?? 'Failed to create quit page reason';
    });

    builder.addCase(updateQuitPageReason.pending, (state) => {
      state.quitPageLoading = false;
      state.quitPageError = null;
    });
    builder.addCase(updateQuitPageReason.fulfilled, (state) => {
      state.quitPageLoading = false;
      state.quitPageError = null;
    });
    builder.addCase(updateQuitPageReason.rejected, (state, action) => {
      state.quitPageLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.quitPageError =
        payload?.message ?? action.error.message ?? 'Failed to update quit page reason';
    });

    builder.addCase(deleteQuitPageReason.pending, (state) => {
      state.quitPageLoading = false;
      state.quitPageError = null;
    });
    builder.addCase(deleteQuitPageReason.fulfilled, (state) => {
      state.quitPageLoading = false;
      state.quitPageError = null;
    });
    builder.addCase(deleteQuitPageReason.rejected, (state, action) => {
      state.quitPageLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.quitPageError =
        payload?.message ?? action.error.message ?? 'Failed to delete quit page reason';
    });

    builder.addCase(uploadQuitPageReasonImage.pending, (state) => {
      state.quitPageLoading = false;
      state.quitPageError = null;
    });
    builder.addCase(
      uploadQuitPageReasonImage.fulfilled,
      (state, action: PayloadAction<QuitPage>) => {
        state.quitPageLoading = false;
        state.currentQuitPage = action.payload;
        state.quitPageError = null;
      },
    );
    builder.addCase(uploadQuitPageReasonImage.rejected, (state, action) => {
      state.quitPageLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.quitPageError =
        payload?.message ?? action.error.message ?? 'Failed to upload quit page reason image';
    });

    builder.addCase(deleteQuitPageReasonImage.pending, (state) => {
      state.quitPageLoading = false;
      state.quitPageError = null;
    });
    builder.addCase(
      deleteQuitPageReasonImage.fulfilled,
      (state, action: PayloadAction<QuitPage>) => {
        state.quitPageLoading = false;
        state.currentQuitPage = action.payload;
        state.quitPageError = null;
      },
    );
    builder.addCase(deleteQuitPageReasonImage.rejected, (state, action) => {
      state.quitPageLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.quitPageError =
        payload?.message ?? action.error.message ?? 'Failed to delete quit page reason image';
    });

    // Webinar Slide reducers
    builder.addCase(listWebinarSlides.pending, (state) => {
      state.webinarSlidesLoading = true;
      state.webinarSlidesError = null;
    });
    builder.addCase(listWebinarSlides.fulfilled, (state, action: PayloadAction<WebinarSlide[]>) => {
      state.webinarSlidesLoading = false;
      state.webinarSlides = action.payload;
      state.webinarSlidesError = null;
    });
    builder.addCase(listWebinarSlides.rejected, (state, action) => {
      state.webinarSlidesLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.webinarSlidesError =
        payload?.message ?? action.error.message ?? 'Failed to list webinar slides';
    });

    builder.addCase(fetchWebinarSlide.pending, (state) => {
      state.currentWebinarSlideLoading = true;
      state.currentWebinarSlideError = null;
    });
    builder.addCase(fetchWebinarSlide.fulfilled, (state, action: PayloadAction<WebinarSlide>) => {
      state.currentWebinarSlideLoading = false;
      state.currentWebinarSlide = action.payload;
      state.currentWebinarSlideError = null;

      const index = state.webinarSlides.findIndex((slide) => slide.id === action.payload.id);
      if (index >= 0) {
        state.webinarSlides[index] = action.payload;
      } else {
        state.webinarSlides.push(action.payload);
      }
    });
    builder.addCase(fetchWebinarSlide.rejected, (state, action) => {
      state.currentWebinarSlideLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.currentWebinarSlideError =
        payload?.message ?? action.error.message ?? 'Failed to fetch webinar slide';
    });

    builder.addCase(generateWebinarSlideUploadUrl.pending, (state) => {
      state.webinarSlideUploadInitLoading = true;
      state.webinarSlideUploadInitError = null;
    });
    builder.addCase(
      generateWebinarSlideUploadUrl.fulfilled,
      (state, action: PayloadAction<SlideUploadInit>) => {
        state.webinarSlideUploadInitLoading = false;
        state.webinarSlideUploadInit = action.payload;
        state.webinarSlideUploadInitError = null;
      },
    );
    builder.addCase(generateWebinarSlideUploadUrl.rejected, (state, action) => {
      state.webinarSlideUploadInitLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.webinarSlideUploadInitError =
        payload?.message ?? action.error.message ?? 'Failed to generate slide upload URL';
    });

    builder.addCase(finalizeWebinarSlideUpload.pending, (state) => {
      state.webinarSlideFinalizeLoading = true;
      state.webinarSlideFinalizeError = null;
    });
    builder.addCase(finalizeWebinarSlideUpload.fulfilled, (state) => {
      state.webinarSlideFinalizeLoading = false;
      state.webinarSlideFinalizeError = null;
      state.webinarSlideUploadInit = null;
    });
    builder.addCase(finalizeWebinarSlideUpload.rejected, (state, action) => {
      state.webinarSlideFinalizeLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.webinarSlideFinalizeError =
        payload?.message ?? action.error.message ?? 'Failed to finalize webinar slide upload';
    });

    builder.addCase(deleteWebinarSlide.pending, (state) => {
      state.webinarSlidesLoading = true;
      state.webinarSlidesError = null;
    });
    builder.addCase(deleteWebinarSlide.fulfilled, (state, action: PayloadAction<number>) => {
      state.webinarSlidesLoading = false;
      state.webinarSlidesError = null;
      state.webinarSlides = state.webinarSlides.filter((slide) => slide.id !== action.payload);
      if (state.currentWebinarSlide?.id === action.payload) {
        state.currentWebinarSlide = null;
      }
    });
    builder.addCase(deleteWebinarSlide.rejected, (state, action) => {
      state.webinarSlidesLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.webinarSlidesError =
        payload?.message ?? action.error.message ?? 'Failed to delete webinar slide';
    });

    builder.addCase(listWebinarSlideNotes.pending, (state) => {
      state.webinarSlideNotesLoading = true;
      state.webinarSlideNotesError = null;
    });
    builder.addCase(
      listWebinarSlideNotes.fulfilled,
      (state, action: PayloadAction<WebinarSlideNote[]>) => {
        state.webinarSlideNotesLoading = false;
        state.webinarSlideNotes = action.payload;
        state.webinarSlideNotesError = null;
      },
    );
    builder.addCase(listWebinarSlideNotes.rejected, (state, action) => {
      state.webinarSlideNotesLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.webinarSlideNotesError =
        payload?.message ?? action.error.message ?? 'Failed to list webinar slide notes';
    });

    builder.addCase(bulkUpsertWebinarSlideNotes.pending, (state) => {
      state.webinarSlideNotesSaving = true;
      state.webinarSlideNotesSavingError = null;
    });
    builder.addCase(bulkUpsertWebinarSlideNotes.fulfilled, (state) => {
      state.webinarSlideNotesSaving = false;
      state.webinarSlideNotesSavingError = null;
    });
    builder.addCase(bulkUpsertWebinarSlideNotes.rejected, (state, action) => {
      state.webinarSlideNotesSaving = false;
      const payload = action.payload as { message?: string } | undefined;
      state.webinarSlideNotesSavingError =
        payload?.message ?? action.error.message ?? 'Failed to save webinar slide notes';
    });

    builder.addCase(updateWebinarSlideNote.pending, (state) => {
      state.webinarSlideNoteUpdating = true;
      state.webinarSlideNoteUpdatingError = null;
    });
    builder.addCase(updateWebinarSlideNote.fulfilled, (state) => {
      state.webinarSlideNoteUpdating = false;
      state.webinarSlideNoteUpdatingError = null;
    });
    builder.addCase(updateWebinarSlideNote.rejected, (state, action) => {
      state.webinarSlideNoteUpdating = false;
      const payload = action.payload as { message?: string } | undefined;
      state.webinarSlideNoteUpdatingError =
        payload?.message ?? action.error.message ?? 'Failed to update webinar slide note';
    });

    // Webinar Resource reducers
    builder.addCase(listWebinarResources.pending, (state) => {
      state.webinarResourcesLoading = true;
      state.webinarResourcesError = null;
    });
    builder.addCase(
      listWebinarResources.fulfilled,
      (
        state,
        action: PayloadAction<{
          content: WebinarResource[];
          totalElements: number;
          totalPages: number;
        }>,
      ) => {
        state.webinarResourcesLoading = false;
        state.webinarResources = action.payload.content;
        state.webinarResourcesTotalElements = action.payload.totalElements;
        state.webinarResourcesTotalPages = action.payload.totalPages;
        state.webinarResourcesError = null;
      },
    );
    builder.addCase(listWebinarResources.rejected, (state, action) => {
      state.webinarResourcesLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.webinarResourcesError =
        payload?.message ?? action.error.message ?? 'Failed to list webinar resources';
    });

    // ---- Fichiers & Liens (SharedResource) ----
    builder.addCase(listSharedResources.pending, (state) => {
      state.sharedResourcesLoading = true;
      state.sharedResourcesError = null;
    });
    builder.addCase(
      listSharedResources.fulfilled,
      (state, action: PayloadAction<SharedResourcePage>) => {
        state.sharedResourcesLoading = false;
        state.sharedResources = [...action.payload.content].sort((a, b) => a.position - b.position);
        state.sharedResourcesPage = action.payload.page;
        state.sharedResourcesTotalPages = action.payload.totalPages;
        state.sharedResourcesTotalElements = action.payload.totalElements;
        state.sharedResourcesError = null;
      },
    );
    builder.addCase(listSharedResources.rejected, (state, action) => {
      state.sharedResourcesLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.sharedResourcesError =
        payload?.message ?? action.error.message ?? 'Failed to list shared resources';
    });
    const upsertSharedResource = (state: WebinarState, resource: SharedResource) => {
      const index = state.sharedResources.findIndex((r) => r.id === resource.id);
      if (index >= 0) {
        state.sharedResources[index] = resource;
      } else {
        state.sharedResources.push(resource);
      }
      state.sharedResources.sort((a, b) => a.position - b.position);
    };
    builder.addCase(
      createSharedResourceLink.fulfilled,
      (state, action: PayloadAction<SharedResource>) => {
        upsertSharedResource(state, action.payload);
      },
    );
    builder.addCase(
      finalizeSharedResourceFileUpload.fulfilled,
      (state, action: PayloadAction<SharedResource>) => {
        upsertSharedResource(state, action.payload);
      },
    );
    builder.addCase(deleteSharedResource.fulfilled, (state, action: PayloadAction<number>) => {
      state.sharedResources = state.sharedResources.filter((r) => r.id !== action.payload);
    });
    // ---- Fichiers & Liens : état live (diffusion) ----
    builder.addCase(
      fetchCurrentSharedResources.fulfilled,
      (state, action: PayloadAction<SharedResourcePublic[]>) => {
        state.currentSharedResources = action.payload;
      },
    );
    builder.addCase(
      adminShowSharedResource.fulfilled,
      (state, action: PayloadAction<SharedResourcePublic>) => {
        const resource = action.payload;
        const index = state.currentSharedResources.findIndex((r) => r.id === resource.id);
        if (index >= 0) {
          state.currentSharedResources[index] = resource;
        } else {
          state.currentSharedResources.push(resource);
        }
      },
    );
    builder.addCase(adminHideSharedResource.fulfilled, (state, action: PayloadAction<number>) => {
      state.currentSharedResources = state.currentSharedResources.filter(
        (r) => r.id !== action.payload,
      );
    });

    builder.addCase(initWebinarResourceUpload.pending, (state) => {
      state.webinarResourceUploadInitLoading = true;
      state.webinarResourceUploadInitError = null;
    });
    builder.addCase(
      initWebinarResourceUpload.fulfilled,
      (state, action: PayloadAction<WebinarResourceUploadInit>) => {
        state.webinarResourceUploadInitLoading = false;
        state.webinarResourceUploadInit = action.payload;
        state.webinarResourceUploadInitError = null;
      },
    );
    builder.addCase(initWebinarResourceUpload.rejected, (state, action) => {
      state.webinarResourceUploadInitLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.webinarResourceUploadInitError =
        payload?.message ?? action.error.message ?? 'Failed to init webinar resource upload';
    });

    builder.addCase(finalizeWebinarResourceUpload.pending, (state) => {
      state.webinarResourceFinalizeLoading = true;
      state.webinarResourceFinalizeError = null;
    });
    builder.addCase(finalizeWebinarResourceUpload.fulfilled, (state) => {
      state.webinarResourceFinalizeLoading = false;
      state.webinarResourceFinalizeError = null;
      state.webinarResourceUploadInit = null;
    });
    builder.addCase(finalizeWebinarResourceUpload.rejected, (state, action) => {
      state.webinarResourceFinalizeLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.webinarResourceFinalizeError =
        payload?.message ?? action.error.message ?? 'Failed to finalize webinar resource upload';
    });

    builder.addCase(deleteWebinarResource.pending, (state) => {
      state.webinarResourcesLoading = true;
      state.webinarResourcesError = null;
    });
    builder.addCase(deleteWebinarResource.fulfilled, (state, action: PayloadAction<number>) => {
      state.webinarResourcesLoading = false;
      state.webinarResourcesError = null;
      state.webinarResources = state.webinarResources.filter(
        (resource) => resource.id !== action.payload,
      );
    });
    builder.addCase(deleteWebinarResource.rejected, (state, action) => {
      state.webinarResourcesLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.webinarResourcesError =
        payload?.message ?? action.error.message ?? 'Failed to delete webinar resource';
    });

    // Survey Template reducers
    builder.addCase(listSurveyTemplates.pending, (state) => {
      state.surveyTemplatesLoading = true;
      state.surveyTemplatesError = null;
    });
    builder.addCase(
      listSurveyTemplates.fulfilled,
      (state, action: PayloadAction<SurveyTemplate[]>) => {
        state.surveyTemplatesLoading = false;
        state.surveyTemplates = action.payload;
        state.surveyTemplatesError = null;
      },
    );
    builder.addCase(listSurveyTemplates.rejected, (state, action) => {
      state.surveyTemplatesLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.surveyTemplatesError =
        payload?.message ?? action.error.message ?? 'Failed to list survey templates';
    });

    builder.addCase(fetchSurveyTemplate.pending, (state) => {
      state.currentSurveyTemplateLoading = true;
      state.currentSurveyTemplateError = null;
    });
    builder.addCase(
      fetchSurveyTemplate.fulfilled,
      (state, action: PayloadAction<SurveyTemplate>) => {
        state.currentSurveyTemplateLoading = false;
        state.currentSurveyTemplate = action.payload;
        state.surveyQuestions = action.payload.questions ?? [];
        // state.surveyAnswerOptions = [];
        state.currentSurveyTemplateError = null;

        const index = state.surveyTemplates.findIndex((s) => s.id === action.payload.id);
        if (index >= 0) {
          state.surveyTemplates[index] = action.payload;
        } else {
          state.surveyTemplates.push(action.payload);
        }
      },
    );
    builder.addCase(fetchSurveyTemplate.rejected, (state, action) => {
      state.currentSurveyTemplateLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.currentSurveyTemplateError =
        payload?.message ?? action.error.message ?? 'Failed to fetch survey template';
    });

    builder.addCase(createSurveyTemplate.pending, (state) => {
      state.surveyTemplatesLoading = false;
      state.surveyTemplatesError = null;
    });
    builder.addCase(createSurveyTemplate.fulfilled, (state) => {
      state.surveyTemplatesLoading = false;
      state.surveyTemplatesError = null;
    });
    builder.addCase(createSurveyTemplate.rejected, (state, action) => {
      state.surveyTemplatesLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.surveyTemplatesError =
        payload?.message ?? action.error.message ?? 'Failed to create survey template';
    });

    builder.addCase(updateSurveyTemplate.pending, (state) => {
      state.currentSurveyTemplateLoading = true;
      state.currentSurveyTemplateError = null;
    });
    builder.addCase(
      updateSurveyTemplate.fulfilled,
      (state, action: PayloadAction<SurveyTemplate>) => {
        state.currentSurveyTemplateLoading = false;
        state.currentSurveyTemplateError = null;

        const index = state.surveyTemplates.findIndex((s) => s.id === action.payload.id);
        if (index >= 0) {
          state.surveyTemplates[index] = action.payload;
        }

        if (state.currentSurveyTemplate?.id === action.payload.id) {
          state.currentSurveyTemplate = action.payload;
        }
      },
    );
    builder.addCase(updateSurveyTemplate.rejected, (state, action) => {
      state.currentSurveyTemplateLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.currentSurveyTemplateError =
        payload?.message ?? action.error.message ?? 'Failed to update survey template';
    });

    builder.addCase(deleteSurveyTemplate.pending, (state) => {
      state.surveyTemplatesLoading = false;
      state.surveyTemplatesError = null;
    });
    builder.addCase(deleteSurveyTemplate.fulfilled, (state, action: PayloadAction<number>) => {
      state.surveyTemplatesLoading = false;
      state.surveyTemplatesError = null;
      state.surveyTemplates = state.surveyTemplates.filter((s) => s.id !== action.payload);
      if (state.currentSurveyTemplate?.id === action.payload) {
        state.currentSurveyTemplate = null;
      }
    });
    builder.addCase(deleteSurveyTemplate.rejected, (state, action) => {
      state.surveyTemplatesLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.surveyTemplatesError =
        payload?.message ?? action.error.message ?? 'Failed to delete survey template';
    });

    // Survey Question reducers
    builder.addCase(listSurveyQuestions.pending, (state) => {
      state.surveyQuestionsLoading = true;
      state.surveyQuestionsError = null;
    });
    builder.addCase(
      listSurveyQuestions.fulfilled,
      (state, action: PayloadAction<SurveyQuestion[]>) => {
        state.surveyQuestionsLoading = false;
        state.surveyQuestions = action.payload;
        state.surveyQuestionsError = null;
      },
    );
    builder.addCase(listSurveyQuestions.rejected, (state, action) => {
      state.surveyQuestionsLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.surveyQuestionsError =
        payload?.message ?? action.error.message ?? 'Failed to list survey questions';
    });

    builder.addCase(createSurveyQuestion.pending, (state) => {
      state.surveyQuestionsLoading = true;
      state.surveyQuestionsError = null;
    });
    builder.addCase(createSurveyQuestion.fulfilled, (state) => {
      state.surveyQuestionsLoading = false;
      state.surveyQuestionsError = null;
    });
    builder.addCase(createSurveyQuestion.rejected, (state, action) => {
      state.surveyQuestionsLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.surveyQuestionsError =
        payload?.message ?? action.error.message ?? 'Failed to create survey question';
    });

    builder.addCase(updateSurveyQuestion.pending, (state) => {
      state.surveyQuestionsLoading = true;
      state.surveyQuestionsError = null;
    });
    builder.addCase(updateSurveyQuestion.fulfilled, (state) => {
      state.surveyQuestionsLoading = false;
      state.surveyQuestionsError = null;
    });
    builder.addCase(updateSurveyQuestion.rejected, (state, action) => {
      state.surveyQuestionsLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.surveyQuestionsError =
        payload?.message ?? action.error.message ?? 'Failed to update survey question';
    });

    builder.addCase(deleteSurveyQuestion.pending, (state) => {
      state.surveyQuestionsLoading = true;
      state.surveyQuestionsError = null;
    });
    builder.addCase(deleteSurveyQuestion.fulfilled, (state) => {
      state.surveyQuestionsLoading = false;
      state.surveyQuestionsError = null;
    });
    builder.addCase(deleteSurveyQuestion.rejected, (state, action) => {
      state.surveyQuestionsLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.surveyQuestionsError =
        payload?.message ?? action.error.message ?? 'Failed to delete survey question';
    });

    // Survey Answer Option reducers
    builder.addCase(listSurveyAnswerOptions.pending, (state) => {
      state.surveyAnswerOptionsLoading = true;
      state.surveyAnswerOptionsError = null;
    });
    builder.addCase(
      listSurveyAnswerOptions.fulfilled,
      (state, action: PayloadAction<SurveyAnswerOption[]>) => {
        state.surveyAnswerOptionsLoading = false;
        state.surveyAnswerOptions = action.payload;
        state.surveyAnswerOptionsError = null;
      },
    );
    builder.addCase(listSurveyAnswerOptions.rejected, (state, action) => {
      state.surveyAnswerOptionsLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.surveyAnswerOptionsError =
        payload?.message ?? action.error.message ?? 'Failed to list survey answer options';
    });

    builder.addCase(createSurveyAnswerOption.pending, (state) => {
      state.surveyAnswerOptionsLoading = true;
      state.surveyAnswerOptionsError = null;
    });
    builder.addCase(createSurveyAnswerOption.fulfilled, (state) => {
      state.surveyAnswerOptionsLoading = false;
      state.surveyAnswerOptionsError = null;
    });
    builder.addCase(createSurveyAnswerOption.rejected, (state, action) => {
      state.surveyAnswerOptionsLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.surveyAnswerOptionsError =
        payload?.message ?? action.error.message ?? 'Failed to create survey answer option';
    });

    builder.addCase(updateSurveyAnswerOption.pending, (state) => {
      state.surveyAnswerOptionsLoading = true;
      state.surveyAnswerOptionsError = null;
    });
    builder.addCase(updateSurveyAnswerOption.fulfilled, (state) => {
      state.surveyAnswerOptionsLoading = false;
      state.surveyAnswerOptionsError = null;
    });
    builder.addCase(updateSurveyAnswerOption.rejected, (state, action) => {
      state.surveyAnswerOptionsLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.surveyAnswerOptionsError =
        payload?.message ?? action.error.message ?? 'Failed to update survey answer option';
    });

    builder.addCase(deleteSurveyAnswerOption.pending, (state) => {
      state.surveyAnswerOptionsLoading = true;
      state.surveyAnswerOptionsError = null;
    });
    builder.addCase(deleteSurveyAnswerOption.fulfilled, (state) => {
      state.surveyAnswerOptionsLoading = false;
      state.surveyAnswerOptionsError = null;
    });
    builder.addCase(deleteSurveyAnswerOption.rejected, (state, action) => {
      state.surveyAnswerOptionsLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.surveyAnswerOptionsError =
        payload?.message ?? action.error.message ?? 'Failed to delete survey answer option';
    });

    // Quiz Template reducers
    builder.addCase(listQuizTemplates.pending, (state) => {
      state.quizTemplatesLoading = true;
      state.quizTemplatesError = null;
    });
    builder.addCase(listQuizTemplates.fulfilled, (state, action: PayloadAction<QuizTemplate[]>) => {
      state.quizTemplatesLoading = false;
      state.quizTemplates = action.payload;
      state.quizTemplatesError = null;
    });
    builder.addCase(listQuizTemplates.rejected, (state, action) => {
      state.quizTemplatesLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.quizTemplatesError =
        payload?.message ?? action.error.message ?? 'Failed to list quiz templates';
    });

    builder.addCase(fetchQuizTemplate.pending, (state) => {
      state.currentQuizTemplateLoading = true;
      state.currentQuizTemplateError = null;
    });
    builder.addCase(fetchQuizTemplate.fulfilled, (state, action: PayloadAction<QuizTemplate>) => {
      state.currentQuizTemplateLoading = false;
      state.currentQuizTemplate = action.payload;
      state.quizQuestions = action.payload.questions ?? [];
      state.quizAnswerOptions = [];
      state.currentQuizTemplateError = null;

      const index = state.quizTemplates.findIndex((q) => q.id === action.payload.id);
      if (index >= 0) {
        state.quizTemplates[index] = action.payload;
      } else {
        state.quizTemplates.push(action.payload);
      }
    });
    builder.addCase(fetchQuizTemplate.rejected, (state, action) => {
      state.currentQuizTemplateLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.currentQuizTemplateError =
        payload?.message ?? action.error.message ?? 'Failed to fetch quiz template';
    });

    builder.addCase(createQuizTemplate.pending, (state) => {
      state.quizTemplatesLoading = false;
      state.quizTemplatesError = null;
    });
    builder.addCase(createQuizTemplate.fulfilled, (state) => {
      state.quizTemplatesLoading = false;
      state.quizTemplatesError = null;
    });
    builder.addCase(createQuizTemplate.rejected, (state, action) => {
      state.quizTemplatesLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.quizTemplatesError =
        payload?.message ?? action.error.message ?? 'Failed to create quiz template';
    });

    builder.addCase(updateQuizTemplate.pending, (state) => {
      state.currentQuizTemplateLoading = true;
      state.currentQuizTemplateError = null;
    });
    builder.addCase(updateQuizTemplate.fulfilled, (state, action: PayloadAction<QuizTemplate>) => {
      state.currentQuizTemplateLoading = false;
      state.currentQuizTemplateError = null;

      const index = state.quizTemplates.findIndex((q) => q.id === action.payload.id);
      if (index >= 0) {
        state.quizTemplates[index] = action.payload;
      }

      if (state.currentQuizTemplate?.id === action.payload.id) {
        state.currentQuizTemplate = action.payload;
      }
    });
    builder.addCase(updateQuizTemplate.rejected, (state, action) => {
      state.currentQuizTemplateLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.currentQuizTemplateError =
        payload?.message ?? action.error.message ?? 'Failed to update quiz template';
    });

    // Pas de drapeau de chargement sur la suppression : il remplacerait la liste par un squelette et
    // démonterait les formulaires des quiz encore en saisie.
    builder.addCase(deleteQuizTemplate.pending, (state) => {
      state.quizTemplatesError = null;
    });
    builder.addCase(deleteQuizTemplate.fulfilled, (state, action: PayloadAction<number>) => {
      state.quizTemplatesLoading = false;
      state.quizTemplatesError = null;
      state.quizTemplates = state.quizTemplates.filter((q) => q.id !== action.payload);
      if (state.currentQuizTemplate?.id === action.payload) {
        state.currentQuizTemplate = null;
      }
    });
    builder.addCase(deleteQuizTemplate.rejected, (state, action) => {
      state.quizTemplatesLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.quizTemplatesError =
        payload?.message ?? action.error.message ?? 'Failed to delete quiz template';
    });

    // Quiz Question reducers
    builder.addCase(listQuizQuestions.pending, (state) => {
      state.quizQuestionsLoading = true;
      state.quizQuestionsError = null;
    });
    builder.addCase(listQuizQuestions.fulfilled, (state, action: PayloadAction<QuizQuestion[]>) => {
      state.quizQuestionsLoading = false;
      state.quizQuestions = action.payload;
      state.quizQuestionsError = null;
    });
    builder.addCase(listQuizQuestions.rejected, (state, action) => {
      state.quizQuestionsLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.quizQuestionsError =
        payload?.message ?? action.error.message ?? 'Failed to list quiz questions';
    });

    builder.addCase(createQuizQuestion.pending, (state) => {
      state.quizQuestionsLoading = true;
      state.quizQuestionsError = null;
    });
    builder.addCase(createQuizQuestion.fulfilled, (state) => {
      state.quizQuestionsLoading = false;
      state.quizQuestionsError = null;
    });
    builder.addCase(createQuizQuestion.rejected, (state, action) => {
      state.quizQuestionsLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.quizQuestionsError =
        payload?.message ?? action.error.message ?? 'Failed to create quiz question';
    });

    builder.addCase(updateQuizQuestion.pending, (state) => {
      state.quizQuestionsLoading = true;
      state.quizQuestionsError = null;
    });
    builder.addCase(updateQuizQuestion.fulfilled, (state) => {
      state.quizQuestionsLoading = false;
      state.quizQuestionsError = null;
    });
    builder.addCase(updateQuizQuestion.rejected, (state, action) => {
      state.quizQuestionsLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.quizQuestionsError =
        payload?.message ?? action.error.message ?? 'Failed to update quiz question';
    });

    builder.addCase(deleteQuizQuestion.pending, (state) => {
      state.quizQuestionsLoading = true;
      state.quizQuestionsError = null;
    });
    builder.addCase(deleteQuizQuestion.fulfilled, (state) => {
      state.quizQuestionsLoading = false;
      state.quizQuestionsError = null;
    });
    builder.addCase(deleteQuizQuestion.rejected, (state, action) => {
      state.quizQuestionsLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.quizQuestionsError =
        payload?.message ?? action.error.message ?? 'Failed to delete quiz question';
    });

    // Quiz Answer Option reducers
    builder.addCase(listQuizAnswerOptions.pending, (state) => {
      state.quizAnswerOptionsLoading = true;
      state.quizAnswerOptionsError = null;
    });
    builder.addCase(
      listQuizAnswerOptions.fulfilled,
      (state, action: PayloadAction<QuizAnswerOption[]>) => {
        state.quizAnswerOptionsLoading = false;
        state.quizAnswerOptions = action.payload;
        state.quizAnswerOptionsError = null;
      },
    );
    builder.addCase(listQuizAnswerOptions.rejected, (state, action) => {
      state.quizAnswerOptionsLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.quizAnswerOptionsError =
        payload?.message ?? action.error.message ?? 'Failed to list quiz answer options';
    });

    builder.addCase(createQuizAnswerOption.pending, (state) => {
      state.quizAnswerOptionsLoading = true;
      state.quizAnswerOptionsError = null;
    });
    builder.addCase(createQuizAnswerOption.fulfilled, (state) => {
      state.quizAnswerOptionsLoading = false;
      state.quizAnswerOptionsError = null;
    });
    builder.addCase(createQuizAnswerOption.rejected, (state, action) => {
      state.quizAnswerOptionsLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.quizAnswerOptionsError =
        payload?.message ?? action.error.message ?? 'Failed to create quiz answer option';
    });

    builder.addCase(updateQuizAnswerOption.pending, (state) => {
      state.quizAnswerOptionsLoading = true;
      state.quizAnswerOptionsError = null;
    });
    builder.addCase(updateQuizAnswerOption.fulfilled, (state) => {
      state.quizAnswerOptionsLoading = false;
      state.quizAnswerOptionsError = null;
    });
    builder.addCase(updateQuizAnswerOption.rejected, (state, action) => {
      state.quizAnswerOptionsLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.quizAnswerOptionsError =
        payload?.message ?? action.error.message ?? 'Failed to update quiz answer option';
    });

    builder.addCase(deleteQuizAnswerOption.pending, (state) => {
      state.quizAnswerOptionsLoading = true;
      state.quizAnswerOptionsError = null;
    });
    builder.addCase(deleteQuizAnswerOption.fulfilled, (state) => {
      state.quizAnswerOptionsLoading = false;
      state.quizAnswerOptionsError = null;
    });
    builder.addCase(deleteQuizAnswerOption.rejected, (state, action) => {
      state.quizAnswerOptionsLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.quizAnswerOptionsError =
        payload?.message ?? action.error.message ?? 'Failed to delete quiz answer option';
    });

    // Speaker reducers
    builder.addCase(listSpeakers.pending, (state) => {
      state.speakersLoading = true;
      state.speakersError = null;
    });
    builder.addCase(listSpeakers.fulfilled, (state, action: PayloadAction<Speaker[]>) => {
      state.speakersLoading = false;
      state.speakers = action.payload;
      state.speakersError = null;
    });
    builder.addCase(listSpeakers.rejected, (state, action) => {
      state.speakersLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.speakersError = payload?.message ?? action.error.message ?? 'Failed to list speakers';
    });

    builder.addCase(fetchSpeakerById.pending, (state) => {
      state.currentSpeakerLoading = true;
      state.currentSpeakerError = null;
    });
    builder.addCase(fetchSpeakerById.fulfilled, (state, action: PayloadAction<Speaker>) => {
      state.currentSpeakerLoading = false;
      state.currentSpeaker = action.payload;
      state.currentSpeakerError = null;

      const index = state.speakers.findIndex((s) => s.id === action.payload.id);
      if (index >= 0) {
        state.speakers[index] = action.payload;
      } else {
        state.speakers.push(action.payload);
      }
    });
    builder.addCase(fetchSpeakerById.rejected, (state, action) => {
      state.currentSpeakerLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.currentSpeakerError =
        payload?.message ?? action.error.message ?? 'Failed to fetch speaker';
    });

    builder.addCase(createSpeaker.pending, (state) => {
      state.speakersLoading = true;
      state.speakersError = null;
    });
    builder.addCase(createSpeaker.fulfilled, (state) => {
      state.speakersLoading = false;
      state.speakersError = null;
    });
    builder.addCase(createSpeaker.rejected, (state, action) => {
      state.speakersLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.speakersError = payload?.message ?? action.error.message ?? 'Failed to create speaker';
    });

    builder.addCase(updateSpeaker.pending, (state) => {
      state.speakersLoading = true;
      state.speakersError = null;
    });
    builder.addCase(updateSpeaker.fulfilled, (state) => {
      state.speakersLoading = false;
      state.speakersError = null;
    });
    builder.addCase(updateSpeaker.rejected, (state, action) => {
      state.speakersLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.speakersError = payload?.message ?? action.error.message ?? 'Failed to update speaker';
    });

    builder.addCase(deleteSpeaker.pending, (state) => {
      state.speakersLoading = true;
      state.speakersError = null;
    });
    builder.addCase(deleteSpeaker.fulfilled, (state) => {
      state.speakersLoading = false;
      state.speakersError = null;
    });
    builder.addCase(deleteSpeaker.rejected, (state, action) => {
      state.speakersLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.speakersError = payload?.message ?? action.error.message ?? 'Failed to delete speaker';
    });

    // Moderator reducers
    builder.addCase(listModerators.pending, (state) => {
      state.moderatorsLoading = true;
      state.moderatorsError = null;
    });
    builder.addCase(listModerators.fulfilled, (state, action: PayloadAction<Moderator[]>) => {
      state.moderatorsLoading = false;
      state.moderators = action.payload;
      state.moderatorsError = null;
    });
    builder.addCase(listModerators.rejected, (state, action) => {
      state.moderatorsLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.moderatorsError =
        payload?.message ?? action.error.message ?? 'Failed to list moderators';
    });

    builder.addCase(fetchModeratorById.pending, (state) => {
      state.currentModeratorLoading = true;
      state.currentModeratorError = null;
    });
    builder.addCase(fetchModeratorById.fulfilled, (state, action: PayloadAction<Moderator>) => {
      state.currentModeratorLoading = false;
      state.currentModerator = action.payload;
      state.currentModeratorError = null;

      const index = state.moderators.findIndex((m) => m.id === action.payload.id);
      if (index >= 0) {
        state.moderators[index] = action.payload;
      } else {
        state.moderators.push(action.payload);
      }
    });
    builder.addCase(fetchModeratorById.rejected, (state, action) => {
      state.currentModeratorLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.currentModeratorError =
        payload?.message ?? action.error.message ?? 'Failed to fetch moderator';
    });

    builder.addCase(createModerator.pending, (state) => {
      state.moderatorsLoading = true;
      state.moderatorsError = null;
    });
    builder.addCase(createModerator.fulfilled, (state) => {
      state.moderatorsLoading = false;
      state.moderatorsError = null;
    });
    builder.addCase(createModerator.rejected, (state, action) => {
      state.moderatorsLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.moderatorsError =
        payload?.message ?? action.error.message ?? 'Failed to create moderator';
    });

    builder.addCase(updateModerator.pending, (state) => {
      state.moderatorsLoading = true;
      state.moderatorsError = null;
    });
    builder.addCase(updateModerator.fulfilled, (state) => {
      state.moderatorsLoading = false;
      state.moderatorsError = null;
    });
    builder.addCase(updateModerator.rejected, (state, action) => {
      state.moderatorsLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.moderatorsError =
        payload?.message ?? action.error.message ?? 'Failed to update moderator';
    });

    builder.addCase(deleteModerator.pending, (state) => {
      state.moderatorsLoading = true;
      state.moderatorsError = null;
    });
    builder.addCase(deleteModerator.fulfilled, (state) => {
      state.moderatorsLoading = false;
      state.moderatorsError = null;
    });
    builder.addCase(deleteModerator.rejected, (state, action) => {
      state.moderatorsLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.moderatorsError =
        payload?.message ?? action.error.message ?? 'Failed to delete moderator';
    });

    // Presenter reducers (LIST, POST, GET, PUT, DELETE)
    builder.addCase(listPresenters.pending, (state) => {
      state.presentersLoading = true;
      state.presentersError = null;
    });
    builder.addCase(listPresenters.fulfilled, (state, action: PayloadAction<Presenter[]>) => {
      state.presentersLoading = false;
      state.presenters = action.payload;
      state.presentersError = null;
    });
    builder.addCase(listPresenters.rejected, (state, action) => {
      state.presentersLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.presentersError =
        payload?.message ?? action.error.message ?? 'Failed to list presenters';
    });

    builder.addCase(createPresenter.pending, (state) => {
      state.currentPresenterLoading = true;
      state.currentPresenterError = null;
    });
    builder.addCase(createPresenter.fulfilled, (state) => {
      state.currentPresenterLoading = false;
      state.currentPresenterError = null;
    });
    builder.addCase(createPresenter.rejected, (state, action) => {
      state.currentPresenterLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.currentPresenterError =
        payload?.message ?? action.error.message ?? 'Failed to create presenter';
    });

    builder.addCase(fetchPresenterById.pending, (state) => {
      state.currentPresenterLoading = true;
      state.currentPresenterError = null;
    });
    builder.addCase(fetchPresenterById.fulfilled, (state, action: PayloadAction<Presenter>) => {
      state.currentPresenterLoading = false;
      state.currentPresenter = action.payload;
      state.currentPresenterError = null;
    });
    builder.addCase(fetchPresenterById.rejected, (state, action) => {
      state.currentPresenterLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.currentPresenterError =
        payload?.message ?? action.error.message ?? 'Failed to fetch presenter';
    });

    builder.addCase(updatePresenter.pending, (state) => {
      state.currentPresenterLoading = true;
      state.currentPresenterError = null;
    });
    builder.addCase(updatePresenter.fulfilled, (state) => {
      state.currentPresenterLoading = false;
      state.currentPresenterError = null;
    });
    builder.addCase(updatePresenter.rejected, (state, action) => {
      state.currentPresenterLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.currentPresenterError =
        payload?.message ?? action.error.message ?? 'Failed to update presenter';
    });

    builder.addCase(deletePresenter.pending, (state) => {
      state.currentPresenterLoading = true;
      state.currentPresenterError = null;
    });
    builder.addCase(deletePresenter.fulfilled, (state, action) => {
      state.currentPresenterLoading = false;
      state.currentPresenterError = null;
      const presenterId = action.meta.arg.presenterId;
      if (state.currentPresenter?.id === presenterId) {
        state.currentPresenter = null;
      }
    });
    builder.addCase(deletePresenter.rejected, (state, action) => {
      state.currentPresenterLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.currentPresenterError =
        payload?.message ?? action.error.message ?? 'Failed to delete presenter';
    });

    builder.addCase(fetchSessionInvites.pending, (state) => {
      state.sessionInvitesLoading = true;
      state.sessionInvitesError = null;
    });
    builder.addCase(
      fetchSessionInvites.fulfilled,
      (state, action: PayloadAction<SessionInvite[]>) => {
        state.sessionInvitesLoading = false;
        state.sessionInvites = action.payload;
        state.sessionInvitesError = null;
      },
    );
    builder.addCase(fetchSessionInvites.rejected, (state, action) => {
      state.sessionInvitesLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.sessionInvitesError =
        payload?.message ?? action.error.message ?? 'Failed to fetch session invites';
    });

    builder.addCase(fetchParticipationStatuses.pending, (state) => {
      state.participationStatusesLoading = true;
      state.participationStatusesError = null;
    });
    builder.addCase(
      fetchParticipationStatuses.fulfilled,
      (state, action: PayloadAction<WebinarParticipationStatusConfigDto>) => {
        state.participationStatusesLoading = false;
        state.participationStatuses = action.payload.statuses;
        state.showParticipationStatus = action.payload.showStatus;
        state.participationStatusesError = null;
      },
    );
    builder.addCase(fetchParticipationStatuses.rejected, (state, action) => {
      state.participationStatusesLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.participationStatusesError =
        payload?.message ?? action.error.message ?? 'Failed to fetch participation statuses';
    });

    builder.addCase(replaceParticipationStatuses.pending, (state) => {
      state.participationStatusesLoading = true;
      state.participationStatusesError = null;
    });
    builder.addCase(replaceParticipationStatuses.fulfilled, (state) => {
      state.participationStatusesLoading = false;
      state.participationStatusesError = null;
    });
    builder.addCase(replaceParticipationStatuses.rejected, (state, action) => {
      state.participationStatusesLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.participationStatusesError =
        payload?.message ?? action.error.message ?? 'Failed to replace participation statuses';
    });

    builder.addCase(fetchIntegrationsMe.pending, (state) => {
      state.integrationsMeLoading = true;
      state.integrationsMeError = null;
    });
    builder.addCase(
      fetchIntegrationsMe.fulfilled,
      (state, action: PayloadAction<TechnicalAuthUser>) => {
        state.integrationsMeLoading = false;
        state.currentIntegrationsMe = action.payload;
        state.integrationsMeError = null;
      },
    );
    builder.addCase(fetchIntegrationsMe.rejected, (state, action) => {
      state.integrationsMeLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.integrationsMeError =
        payload?.message ?? action.error.message ?? 'Failed to fetch integrations me';
    });

    builder.addCase(listWorkspaceAccesses.pending, (state) => {
      state.workspaceAccessesLoading = true;
      state.workspaceAccessesError = null;
    });
    builder.addCase(
      listWorkspaceAccesses.fulfilled,
      (state, action: PayloadAction<WorkspaceTeamMember[]>) => {
        state.workspaceAccessesLoading = false;
        state.workspaceAccesses = action.payload;
        state.workspaceAccessesError = null;
      },
    );
    builder.addCase(listWorkspaceAccesses.rejected, (state, action) => {
      state.workspaceAccessesLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.workspaceAccessesError =
        payload?.message ?? action.error.message ?? 'Failed to list workspace accesses';
    });

    builder.addCase(createWorkspaceInvitation.pending, (state) => {
      state.workspaceAccessesLoading = true;
      state.workspaceAccessesError = null;
    });
    builder.addCase(createWorkspaceInvitation.fulfilled, (state) => {
      state.workspaceAccessesLoading = false;
      state.workspaceAccessesError = null;
    });
    builder.addCase(createWorkspaceInvitation.rejected, (state, action) => {
      state.workspaceAccessesLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.workspaceAccessesError =
        payload?.message ?? action.error.message ?? 'Failed to create workspace invitation';
    });

    builder.addCase(fetchEvaluationTemplate.pending, (state) => {
      state.evaluationTemplateLoading = true;
      state.evaluationTemplateError = null;
      // On repart d'un template vierge à chaque fetch pour ne pas conserver celui d'un
      // autre webinaire (le 404 « pas de template » ne remet pas la valeur à null).
      state.evaluationTemplate = null;
    });
    builder.addCase(
      fetchEvaluationTemplate.fulfilled,
      (state, action: PayloadAction<EvaluationTemplate>) => {
        state.evaluationTemplateLoading = false;
        state.evaluationTemplate = action.payload;
        state.evaluationTemplateError = null;
      },
    );
    builder.addCase(fetchEvaluationTemplate.rejected, (state, action) => {
      state.evaluationTemplateLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.evaluationTemplateError =
        payload?.message ?? action.error.message ?? 'Failed to fetch evaluation template';
    });

    builder.addCase(fetchEvaluationQuestions.pending, (state) => {
      state.evaluationQuestionsLoading = true;
      state.evaluationQuestionsError = null;
    });
    builder.addCase(
      fetchEvaluationQuestions.fulfilled,
      (state, action: PayloadAction<EvaluationQuestion[]>) => {
        state.evaluationQuestionsLoading = false;
        state.evaluationQuestions = action.payload;
        state.evaluationQuestionsError = null;
      },
    );
    builder.addCase(fetchEvaluationQuestions.rejected, (state, action) => {
      state.evaluationQuestionsLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.evaluationQuestionsError =
        payload?.message ?? action.error.message ?? 'Failed to fetch evaluation questions';
    });

    builder.addCase(createEvaluationQuestion.pending, (state) => {
      state.createEvaluationQuestionLoading = true;
      state.createEvaluationQuestionError = null;
    });
    builder.addCase(createEvaluationQuestion.fulfilled, (state) => {
      state.createEvaluationQuestionLoading = false;
      state.createEvaluationQuestionError = null;
    });
    builder.addCase(createEvaluationQuestion.rejected, (state, action) => {
      state.createEvaluationQuestionLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.createEvaluationQuestionError =
        payload?.message ?? action.error.message ?? 'Failed to create evaluation question';
    });

    builder.addCase(updateEvaluationTemplate.pending, (state) => {
      state.updateEvaluationTemplateLoading = true;
      state.updateEvaluationTemplateError = null;
    });
    builder.addCase(updateEvaluationTemplate.fulfilled, (state) => {
      state.updateEvaluationTemplateLoading = false;
      state.updateEvaluationTemplateError = null;
    });
    builder.addCase(updateEvaluationTemplate.rejected, (state, action) => {
      state.updateEvaluationTemplateLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.updateEvaluationTemplateError =
        payload?.message ?? action.error.message ?? 'Failed to update evaluation template';
    });

    builder.addCase(updateEvaluationQuestion.pending, (state) => {
      state.updateEvaluationQuestionLoading = true;
      state.updateEvaluationQuestionError = null;
    });
    builder.addCase(updateEvaluationQuestion.fulfilled, (state) => {
      state.updateEvaluationQuestionLoading = false;
      state.updateEvaluationQuestionError = null;
    });
    builder.addCase(updateEvaluationQuestion.rejected, (state, action) => {
      state.updateEvaluationQuestionLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.updateEvaluationQuestionError =
        payload?.message ?? action.error.message ?? 'Failed to update evaluation question';
    });

    builder.addCase(deleteEvaluationQuestion.pending, (state) => {
      state.deleteEvaluationQuestionLoading = true;
      state.deleteEvaluationQuestionError = null;
    });
    builder.addCase(deleteEvaluationQuestion.fulfilled, (state) => {
      state.deleteEvaluationQuestionLoading = false;
      state.deleteEvaluationQuestionError = null;
    });
    builder.addCase(deleteEvaluationQuestion.rejected, (state, action) => {
      state.deleteEvaluationQuestionLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.deleteEvaluationQuestionError =
        payload?.message ?? action.error.message ?? 'Failed to delete evaluation question';
    });

    builder.addCase(listAutomationTriggers.pending, (state) => {
      state.automationTriggersLoading = true;
      state.automationTriggersError = null;
    });
    builder.addCase(
      listAutomationTriggers.fulfilled,
      (state, action: PayloadAction<WebinarIntegrationTrigger[]>) => {
        state.automationTriggersLoading = false;
        state.automationTriggers = action.payload;
        state.automationTriggersError = null;
      },
    );
    builder.addCase(listAutomationTriggers.rejected, (state, action) => {
      state.automationTriggersLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.automationTriggersError =
        payload?.message ?? action.error.message ?? 'Failed to list automation triggers';
    });

    builder.addCase(createAutomationTrigger.pending, (state) => {
      state.automationTriggersLoading = true;
      state.automationTriggersError = null;
    });
    builder.addCase(createAutomationTrigger.fulfilled, (state) => {
      state.automationTriggersLoading = false;
      state.automationTriggersError = null;
    });
    builder.addCase(createAutomationTrigger.rejected, (state, action) => {
      state.automationTriggersLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.automationTriggersError =
        payload?.message ?? action.error.message ?? 'Failed to create automation trigger';
    });

    builder.addCase(updateAutomationTrigger.pending, (state) => {
      state.updateAutomationTriggerLoading = true;
      state.updateAutomationTriggerError = null;
    });
    builder.addCase(updateAutomationTrigger.fulfilled, (state) => {
      state.updateAutomationTriggerLoading = false;
      state.updateAutomationTriggerError = null;
    });
    builder.addCase(updateAutomationTrigger.rejected, (state, action) => {
      state.updateAutomationTriggerLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.updateAutomationTriggerError =
        payload?.message ?? action.error.message ?? 'Failed to update automation trigger';
    });

    builder.addCase(deleteAutomationTrigger.pending, (state) => {
      state.deleteAutomationTriggerLoading = true;
      state.deleteAutomationTriggerError = null;
    });
    builder.addCase(deleteAutomationTrigger.fulfilled, (state) => {
      state.deleteAutomationTriggerLoading = false;
      state.deleteAutomationTriggerError = null;
    });
    builder.addCase(deleteAutomationTrigger.rejected, (state, action) => {
      state.deleteAutomationTriggerLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.deleteAutomationTriggerError =
        payload?.message ?? action.error.message ?? 'Failed to delete automation trigger';
    });

    builder.addCase(listWebhooks.pending, (state) => {
      state.webhooksLoading = true;
      state.webhooksError = null;
    });
    builder.addCase(listWebhooks.fulfilled, (state, action: PayloadAction<WebhookDto[]>) => {
      state.webhooksLoading = false;
      state.webhooks = action.payload;
      state.webhooksError = null;
    });
    builder.addCase(listWebhooks.rejected, (state, action) => {
      state.webhooksLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.webhooksError = payload?.message ?? action.error.message ?? 'Failed to list webhooks';
    });

    builder.addCase(createWebhook.pending, (state) => {
      state.webhooksLoading = true;
      state.webhooksError = null;
    });
    builder.addCase(createWebhook.fulfilled, (state) => {
      state.webhooksLoading = false;
      state.webhooksError = null;
    });
    builder.addCase(createWebhook.rejected, (state, action) => {
      state.webhooksLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.webhooksError = payload?.message ?? action.error.message ?? 'Failed to create webhook';
    });

    builder.addCase(updateWebhook.pending, (state) => {
      state.updateWebhookLoading = true;
      state.updateWebhookError = null;
    });
    builder.addCase(updateWebhook.fulfilled, (state) => {
      state.updateWebhookLoading = false;
      state.updateWebhookError = null;
    });
    builder.addCase(updateWebhook.rejected, (state, action) => {
      state.updateWebhookLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.updateWebhookError =
        payload?.message ?? action.error.message ?? 'Failed to update webhook';
    });

    builder.addCase(deleteWebhook.pending, (state) => {
      state.deleteWebhookLoading = true;
      state.deleteWebhookError = null;
    });
    builder.addCase(deleteWebhook.fulfilled, (state) => {
      state.deleteWebhookLoading = false;
      state.deleteWebhookError = null;
    });
    builder.addCase(deleteWebhook.rejected, (state, action) => {
      state.deleteWebhookLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.deleteWebhookError =
        payload?.message ?? action.error.message ?? 'Failed to delete webhook';
    });

    builder.addCase(fetchIntegrationApiKeys.pending, (state) => {
      state.integrationApiKeysLoading = true;
      state.integrationApiKeysError = null;
    });
    builder.addCase(
      fetchIntegrationApiKeys.fulfilled,
      (state, action: PayloadAction<IntegrationApiKeyDto[]>) => {
        state.integrationApiKeysLoading = false;
        state.integrationApiKeys = action.payload;
        state.integrationApiKeysError = null;
      },
    );
    builder.addCase(fetchIntegrationApiKeys.rejected, (state, action) => {
      state.integrationApiKeysLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.integrationApiKeysError =
        payload?.message ?? action.error.message ?? 'Failed to fetch integration API keys';
    });

    builder.addCase(createIntegrationApiKey.pending, (state) => {
      state.integrationApiKeysLoading = true;
      state.integrationApiKeysError = null;
    });
    builder.addCase(createIntegrationApiKey.fulfilled, (state) => {
      state.integrationApiKeysLoading = false;
      state.integrationApiKeysError = null;
    });
    builder.addCase(createIntegrationApiKey.rejected, (state, action) => {
      state.integrationApiKeysLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.integrationApiKeysError =
        payload?.message ?? action.error.message ?? 'Failed to create integration API key';
    });

    builder.addCase(rotateIntegrationApiKey.pending, (state) => {
      state.integrationApiKeysLoading = true;
      state.integrationApiKeysError = null;
    });
    builder.addCase(rotateIntegrationApiKey.fulfilled, (state) => {
      state.integrationApiKeysLoading = false;
      state.integrationApiKeysError = null;
    });
    builder.addCase(rotateIntegrationApiKey.rejected, (state, action) => {
      state.integrationApiKeysLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.integrationApiKeysError =
        payload?.message ?? action.error.message ?? 'Failed to rotate integration API key';
    });

    builder.addCase(revokeIntegrationApiKey.pending, (state) => {
      state.integrationApiKeysLoading = true;
      state.integrationApiKeysError = null;
    });
    builder.addCase(revokeIntegrationApiKey.fulfilled, (state) => {
      state.integrationApiKeysLoading = false;
      state.integrationApiKeysError = null;
    });
    builder.addCase(revokeIntegrationApiKey.rejected, (state, action) => {
      state.integrationApiKeysLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.integrationApiKeysError =
        payload?.message ?? action.error.message ?? 'Failed to revoke integration API key';
    });

    builder.addCase(switchWorkspace, () => initialState);
  },
});

export const {
  setCurrentWebinar,
  setCreateWebinarInitialLoading,
  clearCurrentWebinar,
  applySharedResourceLiveEvent,
  clearCurrentSharedResources,
  reset: resetWebinar,
} = slice.actions;
export default slice.reducer;
